import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FileText, Camera, CheckCircle, AlertCircle, X, Wrench, Cloud, CloudOff, AlertTriangle, Sparkles, Package, Loader2 } from 'lucide-react';
import { useLocation as useRouterLocation } from 'react-router-dom';
import { uploadQueueService } from '../../services/upload-queue.service';
import { openNativeCamera } from '../../utils/camera.utils';
import { Camera as CameraComponent } from '../Camera';
import { isIOS, isNativeApp } from '../../utils/platform.utils';
import { useLocation } from '../../contexts/LocationContext';

// Get passed jobId from navigation (e.g., from Job Activity page)
const usePassedJobId = () => {
  const routerLocation = useRouterLocation();
  return routerLocation.state?.jobId as number | undefined;
};
import { CustomSelect } from '../common/CustomSelect';
import { LocationExpiredModal } from '../common/LocationExpiredModal';
// Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx

// Translation object
const translations = {
  en: {
    // Page title
    dailyReport: 'Daily Report',

    // Success messages
    reportSaved: 'Report Saved!',
    uploadingInBackground: 'Uploading in background...',

    // Queue status
    uploading: 'Uploading',
    report: 'report',
    reports: 'reports',
    queued: 'queued',
    failed: 'failed',
    reportsWillUpload: 'Reports will upload automatically when online',

    // Step indicators
    jobAndWork: 'Job & Work',
    truckTickets: 'Truck Tickets',
    loadsHauledOff: 'Loads Hauled Off',
    subcontractors: 'Subcontractors',
    equipment: 'Equipment',
    equipmentIssues: 'Equipment Issues',
    siltFence: 'Silt Fence',
    photos: 'Photos',

    // Info messages
    autoInfoMessage: 'Crew hours, weather, and materials will be automatically added at 8 PM EST',

    // Error messages
    pleaseSelectJob: 'Please select a job',
    pleaseDescribeWork: 'Please describe the work completed',
    userNotLoggedIn: 'User not logged in',
    failedToQueueReport: 'Failed to queue daily report',

    // Step 1: Job & Work
    selectJobDescribeWork: 'Select Job & Describe Work',
    jobSite: 'Job Site',
    loading: 'Loading...',
    notClockedIn: 'Not clocked in',
    autoSelectedFromTime: 'Auto-selected from your current time entry',
    autoSelectedFromLocation: 'Auto-detected from your GPS location',
    workCompletedToday: 'Work Completed Today',
    describeWhatAccomplished: 'Describe what was accomplished today...',
    issuesProblemsDelays: 'Issues / Problems / Delays (Optional)',
    anyProblemsDelays: 'Any problems or delays? (optional)',
    jobNeedsForTomorrow: 'Job Needs for Tomorrow (Optional)',
    selectItemsNeeded: 'Select items needed',
    otherNeeds: 'Other items not listed',
    typeOtherNeeds: 'Type any other items needed...',
    nextTruckTickets: 'Next: Truck Tickets',

    // Step 1.5: Truck Tickets
    truckTicketEntry: 'Truck Ticket Counts',
    didYouScanAll: 'Did you scan all truck tickets today?',
    yesScannedAll: 'Yes - All Scanned',
    noManualEntry: 'No - Enter Manually',
    goScanTickets: 'Go Scan Tickets Now',
    progressSaved: 'Your progress will be saved',
    enterLoadCounts: 'Enter Load Counts by Material',
    fill: 'Fill',
    rock: 'Rock',
    base: 'Base / Limerock',
    asphalt: 'Asphalt',
    other: 'Other',
    loads: 'loads',
    otherMaterialsNotes: 'Other Materials (Optional)',
    describeOtherMaterials: 'Describe other materials (sand, concrete, etc.)',
    nextEquipment: 'Next: Equipment',
    nextLoadsHauledOff: 'Next: Loads Hauled Off',

    // Step 3: Loads Hauled Off
    loadsHauledOffTitle: 'Loads Hauled Off Site',
    dumpTruckLoads: 'Dump Truck Loads',
    dumpsterLoads: 'Dumpster Loads',
    fillDirt: 'Fill Dirt',
    baserock: 'Baserock',
    asphaltMillings: 'Asphalt Millings',
    concreteDump: 'Concrete',
    miscDump: 'Misc',
    concreteDumpster: 'Concrete',
    demolitionDumpster: 'Demolition Debris',
    vegetationDumpster: 'Vegetation',
    totalRemoved: 'Total Removed',
    haulOffNotes: 'Haul Off Notes (Optional)',
    describeHaulOffDetails: 'Describe haul off details (optional)...',
    nextSubcontractors: 'Next: Subcontractors',
    dumpsters: 'dumpsters',
    trucks: 'trucks',

    // Step 4: Subcontractors
    subcontractorsOnSite: 'Subcontractors On Site',
    anySubcontractorsToday: 'Did any subcontractors work on site today?',
    yes: 'Yes',
    no: 'No',
    selectSubcontractors: 'Select Subcontractors',
    subcontractorNotes: 'What work did they do? (Optional)',
    notesForSubcontractor: 'Notes for this subcontractor...',
    noSubcontractorsAssigned: 'No subcontractors assigned to this job',
    nextEquipmentFromSubs: 'Next: Equipment',

    // Step 2: Equipment
    equipmentUsedToday: 'Equipment Used Today',
    searchByEquipment: 'Search by equipment # or name...',
    selectedEquipment: 'Selected Equipment',
    noEquipmentSelected: 'No equipment selected. Search above to add equipment.',
    back: 'Back',
    nextEquipmentIssues: 'Next: Equipment Issues',

    // Step 2.5: Equipment Issues
    anyEquipmentIssues: 'Any Equipment Issues?',
    reportEquipmentProblems: 'Report any equipment problems or needed repairs',
    yesReportIssue: 'Yes, Report Issue',
    noSkipStep: 'No Issues - Skip',
    selectEquipmentForIssue: 'Select Equipment',
    selectEquipmentWithIssue: 'Select equipment with issue...',
    issueDescription: 'Issue Description',
    describeIssue: 'Describe the issue or problem...',
    severity: 'Severity',
    severityLow: 'Low - Minor issue',
    severityMedium: 'Medium - Affects performance',
    severityHigh: 'High - Not operational',
    addIssuePhoto: 'Add Photo (Optional)',
    saveIssue: 'Save Issue',
    reportedIssues: 'Reported Issues',
    removeIssue: 'Remove',
    nextSiltFence: 'Next: Silt Fence',

    // Step 4: Silt Fence Inspection
    siltFenceInspection: 'Silt Fence Inspection',
    siltFenceCondition: 'Silt Fence Condition',
    conditionGood: 'Good - No issues',
    conditionMinorRepair: 'Minor Repair Needed',
    conditionMajorRepair: 'Major Repair Needed',
    conditionReplacement: 'Replacement Required',
    conditionNotPresent: 'Not Present / N/A',
    repairStatus: 'Repair Status',
    repairStatusNone: 'No Action Needed',
    repairStatusNeeds: 'Needs Repair',
    repairStatusRepaired: 'Repaired Today',
    siltFenceNotes: 'Notes (Optional)',
    addNotesAboutCondition: 'Add notes about condition or repairs needed...',
    addSiltFencePhoto: 'Add Photo',
    siltFencePhotos: 'Silt Fence Photos',
    nextPhotos: 'Next: Photos',

    // Step 5: Photos
    dailyPhotos: 'Daily Photos',
    tapToAddPhotos: 'Tap to add photos',
    selectMultiplePhotos: 'You can select multiple photos',
    photo: 'Photo',
    submitReport: 'Submit Report',
    saving: 'Saving...',

    // Materials placeholder
    materialsCalculated: 'Will be calculated at 8 PM EST'
  },
  es: {
    // Page title
    dailyReport: 'Reporte Diario',

    // Success messages
    reportSaved: '¡Reporte Guardado!',
    uploadingInBackground: 'Cargando en segundo plano...',

    // Queue status
    uploading: 'Cargando',
    report: 'reporte',
    reports: 'reportes',
    queued: 'en cola',
    failed: 'fallidos',
    reportsWillUpload: 'Los reportes se cargarán automáticamente cuando esté en línea',

    // Step indicators
    jobAndWork: 'Trabajo y Obra',
    truckTickets: 'Tickets de Camión',
    loadsHauledOff: 'Cargas Retiradas',
    subcontractors: 'Subcontratistas',
    equipment: 'Equipos',
    equipmentIssues: 'Problemas de Equipos',
    siltFence: 'Cerca de Sedimentos',
    photos: 'Fotos',

    // Info messages
    autoInfoMessage: 'Las horas de la cuadrilla, el clima y los materiales se agregarán automáticamente a las 8 PM EST',

    // Error messages
    pleaseSelectJob: 'Por favor seleccione una obra',
    pleaseDescribeWork: 'Por favor describa el trabajo completado',
    userNotLoggedIn: 'Usuario no ha iniciado sesión',
    failedToQueueReport: 'Error al guardar el reporte diario',

    // Step 1: Job & Work
    selectJobDescribeWork: 'Seleccionar Obra y Describir Trabajo',
    jobSite: 'Obra',
    loading: 'Cargando...',
    notClockedIn: 'No ha fichado',
    autoSelectedFromTime: 'Auto-seleccionado de su entrada de tiempo actual',
    autoSelectedFromLocation: 'Detectado automáticamente por su ubicación GPS',
    workCompletedToday: 'Trabajo Completado Hoy',
    describeWhatAccomplished: 'Describa lo que se logró hoy...',
    issuesProblemsDelays: 'Problemas / Retrasos (Opcional)',
    anyProblemsDelays: '¿Algún problema o retraso? (opcional)',
    jobNeedsForTomorrow: 'Necesidades para Mañana (Opcional)',
    selectItemsNeeded: 'Seleccione artículos necesarios',
    otherNeeds: 'Otros artículos no listados',
    typeOtherNeeds: 'Escriba otros artículos necesarios...',
    nextTruckTickets: 'Siguiente: Tickets de Camión',

    // Step 1.5: Truck Tickets
    truckTicketEntry: 'Conteo de Tickets de Camión',
    didYouScanAll: '¿Escaneó todos los tickets de camión hoy?',
    yesScannedAll: 'Sí - Todos Escaneados',
    noManualEntry: 'No - Ingresar Manualmente',
    goScanTickets: 'Ir a Escanear Tickets Ahora',
    progressSaved: 'Su progreso será guardado',
    enterLoadCounts: 'Ingrese Conteo de Cargas por Material',
    fill: 'Relleno',
    rock: 'Roca',
    base: 'Base / Limerock',
    asphalt: 'Asfalto',
    other: 'Otro',
    loads: 'cargas',
    otherMaterialsNotes: 'Otros Materiales (Opcional)',
    describeOtherMaterials: 'Describa otros materiales (arena, concreto, etc.)',
    nextEquipment: 'Siguiente: Equipos',
    nextLoadsHauledOff: 'Siguiente: Cargas Retiradas',

    // Step 3: Loads Hauled Off
    loadsHauledOffTitle: 'Cargas Retiradas del Sitio',
    dumpTruckLoads: 'Cargas de Camión Volquete',
    dumpsterLoads: 'Contenedores',
    fillDirt: 'Tierra de Relleno',
    baserock: 'Base de Piedra',
    asphaltMillings: 'Fresado de Asfalto',
    concreteDump: 'Concreto',
    miscDump: 'Varios',
    concreteDumpster: 'Concreto',
    demolitionDumpster: 'Escombros de Demolición',
    vegetationDumpster: 'Vegetación',
    totalRemoved: 'Total Retirado',
    haulOffNotes: 'Notas de Retiro (Opcional)',
    describeHaulOffDetails: 'Describa detalles del retiro (opcional)...',
    nextSubcontractors: 'Siguiente: Subcontratistas',
    dumpsters: 'contenedores',
    trucks: 'camiones',

    // Step 4: Subcontractors
    subcontractorsOnSite: 'Subcontratistas en el Sitio',
    anySubcontractorsToday: '¿Trabajaron subcontratistas en el sitio hoy?',
    yes: 'Sí',
    no: 'No',
    selectSubcontractors: 'Seleccionar Subcontratistas',
    subcontractorNotes: '¿Qué trabajo hicieron? (Opcional)',
    notesForSubcontractor: 'Notas para este subcontratista...',
    noSubcontractorsAssigned: 'No hay subcontratistas asignados a este trabajo',
    nextEquipmentFromSubs: 'Siguiente: Equipos',

    // Step 2: Equipment
    equipmentUsedToday: 'Equipos Usados Hoy',
    searchByEquipment: 'Buscar por # de equipo o nombre...',
    selectedEquipment: 'Equipos Seleccionados',
    noEquipmentSelected: 'No hay equipos seleccionados. Busque arriba para agregar equipos.',
    back: 'Atrás',
    nextEquipmentIssues: 'Siguiente: Problemas de Equipos',

    // Step 2.5: Equipment Issues
    anyEquipmentIssues: '¿Algún Problema con Equipos?',
    reportEquipmentProblems: 'Reportar problemas de equipos o reparaciones necesarias',
    yesReportIssue: 'Sí, Reportar Problema',
    noSkipStep: 'Sin Problemas - Omitir',
    selectEquipmentForIssue: 'Seleccionar Equipo',
    selectEquipmentWithIssue: 'Seleccionar equipo con problema...',
    issueDescription: 'Descripción del Problema',
    describeIssue: 'Describa el problema...',
    severity: 'Gravedad',
    severityLow: 'Baja - Problema menor',
    severityMedium: 'Media - Afecta rendimiento',
    severityHigh: 'Alta - No operacional',
    addIssuePhoto: 'Agregar Foto (Opcional)',
    saveIssue: 'Guardar Problema',
    reportedIssues: 'Problemas Reportados',
    removeIssue: 'Eliminar',
    nextSiltFence: 'Siguiente: Cerca de Sedimentos',

    // Step 4: Silt Fence Inspection
    siltFenceInspection: 'Inspección de Cerca de Sedimentos',
    siltFenceCondition: 'Condición de la Cerca',
    conditionGood: 'Buena - Sin problemas',
    conditionMinorRepair: 'Reparación Menor Necesaria',
    conditionMajorRepair: 'Reparación Mayor Necesaria',
    conditionReplacement: 'Reemplazo Requerido',
    conditionNotPresent: 'No Presente / N/A',
    repairStatus: 'Estado de Reparación',
    repairStatusNone: 'No Se Necesita Acción',
    repairStatusNeeds: 'Necesita Reparación',
    repairStatusRepaired: 'Reparado Hoy',
    siltFenceNotes: 'Notas (Opcional)',
    addNotesAboutCondition: 'Agregar notas sobre la condición o reparaciones necesarias...',
    addSiltFencePhoto: 'Agregar Foto',
    siltFencePhotos: 'Fotos de Cerca de Sedimentos',
    nextPhotos: 'Siguiente: Fotos',

    // Step 5: Photos
    dailyPhotos: 'Fotos Diarias',
    tapToAddPhotos: 'Toque para agregar fotos',
    selectMultiplePhotos: 'Puede seleccionar varias fotos',
    photo: 'Foto',
    submitReport: 'Enviar Reporte',
    saving: 'Guardando...',

    // Materials placeholder
    materialsCalculated: 'Se calculará a las 8 PM EST'
  }
};

interface Job {
  job_id: number;
  job_name: string;
  job_number?: string;
  bradanna_job_number?: string;
  address?: string;
  lat?: number;
  lng?: number;
  geofence_type?: string;
  geofence_data?: any;
}

interface Equipment {
  equipment_id: number;
  equipment_name: string;
  serial_number?: string;
  bradanna_equipment_number?: string;
}

interface EquipmentIssue {
  issue_id?: number; // If set, this issue already exists in DB (don't re-submit)
  equipment_id: number;
  equipment_name: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
  photos?: File[]; // Support multiple photos
}

interface RentalEquipmentItem {
  rental_company: string;
  rental_equipment_type: string;
}

// Photo with background upload tracking
interface PhotoWithUpload {
  file: File;
  previewUrl: string;
  uploadStatus: 'pending' | 'uploading' | 'uploaded' | 'failed';
  uploadedUrl?: string; // Full URL after upload
  thumbnailUrl?: string; // Thumbnail URL after upload
}

const API_BASE_URL = import.meta.env.VITE_API_URL;

const DailyReports: React.FC = () => {
  // Get location for auto-detecting job site (useful for admins filling out reports on site)
  const { location, requestLocation, forceRefreshLocation } = useLocation();

  // Get passed jobId from navigation (e.g., from Job Activity page)
  const passedJobId = usePassedJobId();

  const [language, setLanguage] = useState<'en' | 'es'>('en');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState({
    user: true,
    jobs: true,
    equipment: true,
    initializing: true,
    report: false  // Track when report data is loading to prevent user typing before data loads (starts false, set true when job selected)
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [locationDetectedJob, setLocationDetectedJob] = useState(false); // Track if job was auto-detected from location

  // Data
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [selectedJobName, setSelectedJobName] = useState<string>('');
  const [workDescription, setWorkDescription] = useState('');
  const [issues, setIssues] = useState('');
  const [allEquipment, setAllEquipment] = useState<Equipment[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<number[]>([]);
  const [jobSearch, setJobSearch] = useState('');
  const [filteredJobs, setFilteredJobs] = useState<any[]>([]);
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [photoUploads, setPhotoUploads] = useState<PhotoWithUpload[]>([]); // Track background uploads
  const [existingDailyPhotos, setExistingDailyPhotos] = useState<string[]>([]); // Previously uploaded daily photos
  const [queueStats, setQueueStats] = useState({ pending: 0, uploading: 0, failed: 0, completed: 0, total: 0 });
  const [user, setUser] = useState<any>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [currentUploadId, setCurrentUploadId] = useState<number | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'uploading' | 'completed' | 'failed' | 'timeout'>('uploading');
  const [existingReportId, setExistingReportId] = useState<number | null>(null);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  const [loadedFromPendingUpload, setLoadedFromPendingUpload] = useState(false); // Track if data was recovered from pending upload queue
  const [cameraCooldown, setCameraCooldown] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [currentCameraType, setCurrentCameraType] = useState<'daily' | 'issue' | 'siltFence'>('daily');

  // Equipment Issues state
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [equipmentIssues, setEquipmentIssues] = useState<EquipmentIssue[]>([]);
  const [currentIssue, setCurrentIssue] = useState<{
    equipment_id: number | null;
    description: string;
    severity: 'low' | 'medium' | 'high';
    photos: File[];
  }>({
    equipment_id: null,
    description: '',
    severity: 'medium',
    photos: []
  });
  const [currentIssuePhotoPreviews, setCurrentIssuePhotoPreviews] = useState<string[]>([]);
  const [issueCameraCooldown, setIssueCameraCooldown] = useState(false);

  // Rental Equipment state
  const [selectedRentalEquipment, setSelectedRentalEquipment] = useState<RentalEquipmentItem[]>([]);
  const [showRentalForm, setShowRentalForm] = useState(false);
  const [rentalCompany, setRentalCompany] = useState('');
  const [rentalEquipmentType, setRentalEquipmentType] = useState('');

  const rentalCompanies = ['Kelly Tractor', 'United Rentals', 'Synergy', 'Sunbelt', 'Nortrax', 'Alta', 'Other'];
  const rentalEquipmentTypes = ['Excavator', 'Mini Excavator', 'Skid Steer', 'Backhoe', 'Dozer', 'Loader', 'Grader', 'Paver', 'Roller', 'Dump Truck', 'Flatbed', 'Crane', 'Forklift', 'Scissor Lift', 'Boom Lift', 'Generator', 'Compressor', 'Light Tower', 'Trailer', 'Other'];

  // Silt Fence states
  const [siltFenceCondition, setSiltFenceCondition] = useState<string>('');
  const [siltFenceRepairStatus, setSiltFenceRepairStatus] = useState<string>('none'); // 'none', 'needs_repair', 'repaired'
  const [siltFenceNotes, setSiltFenceNotes] = useState<string>('');
  const [siltFencePhotos, setSiltFencePhotos] = useState<File[]>([]);
  const [existingSiltFencePhotos, setExistingSiltFencePhotos] = useState<string[]>([]); // Previously uploaded silt fence photos

  // AI Text Improvement states
  const [aiImproving, setAiImproving] = useState<{[key: string]: boolean}>({});
  const [aiIndicators, setAiIndicators] = useState<{[key: string]: 'translated' | 'improved' | null}>({});
  const [aiSuggestions, setAiSuggestions] = useState<{[key: string]: string}>({});
  const [siltFencePhotoPreviews, setSiltFencePhotoPreviews] = useState<string[]>([]);
  const [siltFenceCameraCooldown, setSiltFenceCameraCooldown] = useState(false);

  // Job Needs states
  const [jobNeedsItems, setJobNeedsItems] = useState<any[]>([]); // Available items from API
  const [selectedJobNeeds, setSelectedJobNeeds] = useState<number[]>([]); // Selected item IDs
  const [jobNeedsOtherText, setJobNeedsOtherText] = useState<string>(''); // Custom text field
  const [showJobNeedsModal, setShowJobNeedsModal] = useState(false); // Modal visibility
  const [jobNeedsSearch, setJobNeedsSearch] = useState<string>(''); // Search filter

  // Truck Ticket Manual Entry states
  const [showTicketScanQuestion, setShowTicketScanQuestion] = useState(false); // Setting from admin - when OFF, always use manual entry
  const [manualTicketEntryEnabled, setManualTicketEntryEnabled] = useState(false); // User chose manual entry
  const [ticketsAlreadyScanned, setTicketsAlreadyScanned] = useState(false); // Track if tickets were already scanned today
  const [adminNeedsLoads, setAdminNeedsLoads] = useState<boolean | null>(null); // Admin decision: include loads in report?
  const [manualLoadsFill, setManualLoadsFill] = useState<number>(0);
  const [manualLoadsRock, setManualLoadsRock] = useState<number>(0);
  const [manualLoadsBase, setManualLoadsBase] = useState<number>(0);
  const [manualLoadsAsphalt, setManualLoadsAsphalt] = useState<number>(0);
  const [manualLoadsOther, setManualLoadsOther] = useState<number>(0);
  const [manualLoadsNotes, setManualLoadsNotes] = useState<string>('');

  // Loads Hauled Off states
  const [haulOffFillDirt, setHaulOffFillDirt] = useState<number>(0);
  const [haulOffBaserock, setHaulOffBaserock] = useState<number>(0);
  const [haulOffAsphalt, setHaulOffAsphalt] = useState<number>(0);
  const [haulOffAsphaltMillings, setHaulOffAsphaltMillings] = useState<number>(0);
  const [haulOffConcreteDump, setHaulOffConcreteDump] = useState<number>(0);
  const [haulOffMiscDump, setHaulOffMiscDump] = useState<number>(0);
  const [haulOffConcreteDumpster, setHaulOffConcreteDumpster] = useState<number>(0);
  const [haulOffDemolitionDumpster, setHaulOffDemolitionDumpster] = useState<number>(0);
  const [haulOffVegetationDumpster, setHaulOffVegetationDumpster] = useState<number>(0);
  const [haulOffNotes, setHaulOffNotes] = useState<string>('');

  // Subcontractors states
  const [allSubcontractorTypes, setAllSubcontractorTypes] = useState<any[]>([]); // All types from API
  const [jobSubcontractors, setJobSubcontractors] = useState<any[]>([]); // Job-specific assignments
  const [subcontractorDisplayList, setSubcontractorDisplayList] = useState<any[]>([]); // Merged list for display
  const [selectedSubcontractors, setSelectedSubcontractors] = useState<number[]>([]); // Selected type_ids
  const [subcontractorNotes, setSubcontractorNotes] = useState<{[key: number]: string}>({}); // Notes per type_id
  const [hadSubcontractors, setHadSubcontractors] = useState<boolean | null>(null); // Did any work today?
  // Work item templates and values
  const [workItemTemplates, setWorkItemTemplates] = useState<{[typeId: number]: any[]}>({});
  const [subcontractorWorkItems, setSubcontractorWorkItems] = useState<{[typeId: number]: {[templateId: number]: any[]}}>({});

  // Location state for PWA location refresh
  const [locationRefreshing, setLocationRefreshing] = useState(false);
  // Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx

  // Helper function to get the report date in EST timezone
  // Night work window (8 PM - 7 AM): report_date = next day (aligns with 8 PM cron)
  //   8 PM - midnight: report_date = tomorrow
  //   midnight - 7 AM: report_date = today (already next day)
  //   7 AM - 8 PM: report_date = today (normal flow)
  const getReportDateString = (): string => {
    const nowEST = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const hourEST = nowEST.getHours();

    // For 8 PM - midnight, use tomorrow's date
    if (hourEST >= 20) {
      nowEST.setDate(nowEST.getDate() + 1);
    }
    // For midnight - 7 AM and 7 AM+, use today's date (already correct)

    const year = nowEST.getFullYear();
    const month = String(nowEST.getMonth() + 1).padStart(2, '0');
    const day = String(nowEST.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Detect if current time is in the night work window
  const getNightWorkStatus = () => {
    const nowEST = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const hourEST = nowEST.getHours();
    return hourEST >= 20 || hourEST < 7;
  };

  // Backward compat alias
  const getTodayDateString = getReportDateString;

  // Get translations with fallback to English
  const t = translations[language] || translations.en;

  // Track visibility changes to re-check location staleness when PWA resumes
  const [visibilityKey, setVisibilityKey] = useState(0);

  // Listen for page visibility changes (iPad sleep/wake, tab switch)
  useEffect(() => {
    if (isNativeApp()) return; // Native apps handle this in LocationContext

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Daily Reports: Page became visible, re-checking location staleness');
        setVisibilityKey(prev => prev + 1); // Force re-check
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Check for stale location (> 5 minutes old) - PWA only
  const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes
  const isLocationStale = useMemo(() => {
    if (isNativeApp()) return false; // Native apps auto-refresh on resume
    if (!location) return true;
    return (Date.now() - location.timestamp) > STALE_THRESHOLD;
  }, [location, visibilityKey]); // Re-check when page becomes visible

  // Note: Job mismatch detection and modal is handled globally by GlobalMismatchChecker in App.tsx

  // Handler for refreshing location (PWA)
  const handleRefreshLocation = async () => {
    setLocationRefreshing(true);
    try {
      // Pass true to bypass 30-second cache optimization - user explicitly wants fresh GPS
      await forceRefreshLocation(true);
    } finally {
      setLocationRefreshing(false);
    }
  };

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      // event.detail is the language string directly (not an object)
      const newLang = (event as CustomEvent).detail;
      // Only set language if it's valid
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };

    window.addEventListener('languageChange', handleLanguageChange);
    const savedLanguage = localStorage.getItem('language') as 'en' | 'es' | null;
    if (savedLanguage === 'en' || savedLanguage === 'es') {
      setLanguage(savedLanguage);
    }

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem('bfa_user');
    if (userData) {
      setUser(JSON.parse(userData));
      setLoadingStates(prev => ({ ...prev, user: false }));
    }

    // Load jobs, equipment, and job needs first, then detect current job
    const initializeData = async () => {
      try {
        const jobsList = await loadJobs();
        setLoadingStates(prev => ({ ...prev, jobs: false }));

        await loadEquipment();
        setLoadingStates(prev => ({ ...prev, equipment: false }));

        await loadJobNeedsItems();
        await loadManualTicketEntrySetting();
        await loadSubcontractorTypes();

        // Pass jobs directly to avoid stale state
        await loadCurrentJob(jobsList);

        // All initialization complete
        setLoadingStates(prev => ({ ...prev, initializing: false }));
      } catch (err) {
        console.error('Error initializing data:', err);
        setLoadingStates(prev => ({ ...prev, initializing: false }));
      }
    };
    initializeData();

    // Initialize upload queue service
    uploadQueueService.startAutoSync();

    const updateStats = async () => {
      const stats = await uploadQueueService.getQueueStats();
      setQueueStats(stats);
    };
    updateStats();
    const statsInterval = setInterval(updateStats, 5000);

    return () => {
      clearInterval(statsInterval);
      // Don't stop sync here - it's managed app-wide now

      // Clean up object URLs to prevent memory leaks
      photoPreviews.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  // Use ref to track current values without causing re-renders
  const formDataRef = useRef({
    selectedJobId,
    selectedJobName,
    workDescription,
    issues,
    selectedEquipment,
    selectedRentalEquipment,
    equipmentIssues,
    siltFenceCondition,
    siltFenceRepairStatus,
    siltFenceNotes,
    selectedJobNeeds,
    jobNeedsOtherText
  });

  // Track if form is being submitted to prevent beforeunload warning
  const isSubmittingRef = useRef(false);

  // Update ref when values change (doesn't cause re-render)
  useEffect(() => {
    formDataRef.current = {
      selectedJobId,
      selectedJobName,
      workDescription,
      issues,
      selectedEquipment,
      selectedRentalEquipment,
      equipmentIssues,
      siltFenceCondition,
      siltFenceRepairStatus,
      siltFenceNotes,
      selectedJobNeeds,
      jobNeedsOtherText
    };
  }, [selectedJobId, selectedJobName, workDescription, issues, selectedEquipment, selectedRentalEquipment, equipmentIssues, siltFenceCondition, siltFenceRepairStatus, siltFenceNotes, selectedJobNeeds, jobNeedsOtherText]);

  // Register beforeunload handler once (doesn't re-register on every keystroke)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Don't show warning if form is being submitted
      if (isSubmittingRef.current) {
        return;
      }

      const data = formDataRef.current;

      // Only save if there's meaningful data entered
      if (data.selectedJobId && (data.workDescription.trim() || data.selectedRentalEquipment.length > 0 || data.equipmentIssues.length > 0)) {
        saveDraftToLocalStorage(data);
        // Some browsers show a confirmation dialog
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // Empty dependency array - only register once

  // Handle Android back button to navigate between steps
  useEffect(() => {
    const handleBackButton = (e: PopStateEvent) => {
      e.preventDefault();

      // If we're on a step > 1, go back one step
      if (step > 1) {
        console.log(`⬅️ Back button: Going from step ${step} to step ${step - 1}`);
        setStep(step - 1);

        // Push a new state so back button can work again
        window.history.pushState({ step: step - 1 }, '');
      } else {
        // On step 1, allow normal back navigation (will unmount component)
        console.log('⬅️ Back button: On step 1, allowing navigation away');
        // Component unmount effect will save draft
      }
    };

    // Push initial state so we can intercept back button
    window.history.pushState({ step }, '');
    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [step]);

  // Save draft when component unmounts (user navigates away with back button)
  useEffect(() => {
    return () => {
      // Component is unmounting (user navigated away)
      const data = formDataRef.current;

      // Save if there's meaningful data and not after successful submission
      if (!success && data.selectedJobId && (data.workDescription.trim() || data.selectedRentalEquipment.length > 0 || data.equipmentIssues.length > 0)) {
        console.log('💾 Auto-saving draft on navigation...');
        saveDraftToLocalStorage(data);
      }
    };
  }, [success]); // Include success to prevent saving after submission

  useEffect(() => {
    if (selectedJobId && allSubcontractorTypes.length > 0) {
      // Load job-specific subcontractors when job and types are both available
      loadJobSubcontractors(selectedJobId);
    }
  }, [selectedJobId, allSubcontractorTypes]);

  // Load work item templates when subcontractors are selected
  useEffect(() => {
    if (selectedSubcontractors.length > 0) {
      loadWorkItemTemplatesForTypes(selectedSubcontractors);
    }
  }, [selectedSubcontractors]);

  useEffect(() => {
    if (selectedJobId && user) {
      // Load today's report whenever job changes
      console.log(`📋 Job changed to ${selectedJobId}, reloading report data...`);

      // Reset form data when job changes
      setWorkDescription('');
      setIssues('');
      setSelectedEquipment([]);
      setEquipmentIssues([]);
      setSelectedRentalEquipment([]);
      setShowRentalForm(false);
      setRentalCompany('');
      setRentalEquipmentType('');
      setSiltFenceCondition('');
      setSiltFenceRepairStatus('none');
      setSiltFenceNotes('');
      setSiltFencePhotos([]);
      setSiltFencePhotoPreviews([]);
      setPhotos([]);
      setPhotoPreviews([]);
      setPhotoUploads([]);
      setExistingReportId(null);
      setStep(1);

      // Reset manual ticket entry fields
      setManualTicketEntryEnabled(false);
      setManualLoadsFill(0);
      setManualLoadsRock(0);
      setManualLoadsBase(0);
      setManualLoadsAsphalt(0);
      setManualLoadsOther(0);
      setManualLoadsNotes('');

      // Reset hauled off loads
      setHaulOffFillDirt(0);
      setHaulOffBaserock(0);
      setHaulOffAsphalt(0);
      setHaulOffAsphaltMillings(0);
      setHaulOffConcreteDump(0);
      setHaulOffMiscDump(0);
      setHaulOffConcreteDumpster(0);
      setHaulOffDemolitionDumpster(0);
      setHaulOffVegetationDumpster(0);
      setHaulOffNotes('');

      // Reset subcontractors
      setHadSubcontractors(false);
      setSelectedSubcontractors([]);
      setSubcontractorNotes({});

      // Reset ticket scan status
      setTicketsAlreadyScanned(false);

      // Mark report as loading before fetching (prevents user typing before data loads)
      setLoadingStates(prev => ({ ...prev, report: true }));

      // Load report data for new job
      loadTodaysReport();
      // Load rental equipment (from truck deliveries + previous reports)
      loadRentalEquipment();
      // Check if tickets have already been scanned for this job today
      checkTicketsAlreadyScanned(selectedJobId);
      setHasLoadedInitialData(true);
    }
  }, [selectedJobId, user]);

  // Auto-save draft periodically while working (every 30 seconds)
  useEffect(() => {
    if (!selectedJobId || success) return; // Don't auto-save after successful submission

    const autoSaveInterval = setInterval(() => {
      if (workDescription.trim() || selectedRentalEquipment.length > 0 || equipmentIssues.length > 0 ||
          selectedJobNeeds.length > 0 || jobNeedsOtherText.trim() ||
          siltFenceCondition || siltFenceNotes.trim()) {
        saveDraftToLocalStorage();
      }
    }, 30000); // 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [selectedJobId, workDescription, selectedRentalEquipment, equipmentIssues, selectedJobNeeds, jobNeedsOtherText, siltFenceCondition, siltFenceNotes, success]);


  useEffect(() => {
    if (jobSearch.trim()) {
      const filtered = jobs.filter(job =>
        job.job_name.toLowerCase().includes(jobSearch.toLowerCase()) ||
        job.job_number?.toLowerCase().includes(jobSearch.toLowerCase()) ||
        job.address?.toLowerCase().includes(jobSearch.toLowerCase())
      );
      setFilteredJobs(filtered);
    } else {
      setFilteredJobs([]);
    }
  }, [jobSearch, jobs]);

  // Load shared manual counts when manual entry is enabled
  useEffect(() => {
    if (selectedJobId && manualTicketEntryEnabled) {
      loadSharedManualCounts();
    }
  }, [selectedJobId, manualTicketEntryEnabled]);

  // Auto-save shared manual counts with 1-second debounce
  useEffect(() => {
    if (!manualTicketEntryEnabled) return;

    const timer = setTimeout(() => {
      saveSharedManualCounts();
    }, 1000); // 1 second debounce

    return () => clearTimeout(timer);
  }, [manualLoadsFill, manualLoadsRock, manualLoadsBase, manualLoadsAsphalt, manualLoadsOther, manualLoadsNotes, manualTicketEntryEnabled]);

  // Auto-save/load draft functionality
  const saveDraftToLocalStorage = (data?: {
    selectedJobId: number | null;
    selectedJobName: string;
    workDescription: string;
    issues: string;
    selectedEquipment: number[];
    selectedRentalEquipment?: RentalEquipmentItem[];
    equipmentIssues: any[];
    siltFenceCondition: string;
    siltFenceRepairStatus: string;
    siltFenceNotes: string;
    selectedJobNeeds: number[];
    jobNeedsOtherText: string;
  }) => {
    // Use passed data or current state
    const jobId = data?.selectedJobId || selectedJobId;
    if (!jobId || !user?.employee_id) return;

    const draft = {
      employeeId: user.employee_id, // Store for validation on load
      jobId: jobId,
      jobName: data?.selectedJobName || selectedJobName,
      workDescription: data?.workDescription || workDescription,
      issues: data?.issues || issues,
      selectedEquipment: data?.selectedEquipment || selectedEquipment,
      selectedRentalEquipment: data?.selectedRentalEquipment || selectedRentalEquipment,
      equipmentIssues: (data?.equipmentIssues || equipmentIssues).map(issue => ({
        ...issue,
        photos: [] // Can't save File objects to localStorage
      })),
      siltFenceCondition: data?.siltFenceCondition || siltFenceCondition,
      siltFenceRepairStatus: data?.siltFenceRepairStatus || siltFenceRepairStatus,
      siltFenceNotes: data?.siltFenceNotes || siltFenceNotes,
      selectedJobNeeds: data?.selectedJobNeeds || selectedJobNeeds,
      jobNeedsOtherText: data?.jobNeedsOtherText || jobNeedsOtherText,
      // Manual truck ticket entry
      manualTicketEntryEnabled: manualTicketEntryEnabled,
      manualLoadsFill: manualLoadsFill,
      manualLoadsRock: manualLoadsRock,
      manualLoadsBase: manualLoadsBase,
      manualLoadsAsphalt: manualLoadsAsphalt,
      manualLoadsOther: manualLoadsOther,
      manualLoadsNotes: manualLoadsNotes,
      // Loads hauled off
      haulOffFillDirt: haulOffFillDirt,
      haulOffBaserock: haulOffBaserock,
      haulOffAsphalt: haulOffAsphalt,
      haulOffAsphaltMillings: haulOffAsphaltMillings,
      haulOffConcreteDump: haulOffConcreteDump,
      haulOffMiscDump: haulOffMiscDump,
      haulOffConcreteDumpster: haulOffConcreteDumpster,
      haulOffDemolitionDumpster: haulOffDemolitionDumpster,
      haulOffVegetationDumpster: haulOffVegetationDumpster,
      haulOffNotes: haulOffNotes,
      // Subcontractors
      hadSubcontractors: hadSubcontractors,
      selectedSubcontractors: selectedSubcontractors,
      subcontractorNotes: subcontractorNotes,
      subcontractorWorkItems: subcontractorWorkItems,
      timestamp: new Date().toISOString()
    };

    const draftKey = `daily_report_draft_${user.employee_id}_${jobId}`;
    console.log('💾 Saving draft with key:', draftKey);
    if (!user.employee_id || user.employee_id === 'undefined') {
      console.error('❌ CRITICAL: Attempting to save draft with invalid employee_id!', user);
      return; // Don't save drafts with invalid keys
    }
    localStorage.setItem(draftKey, JSON.stringify(draft));
    console.log('💾 Draft saved to localStorage for user', user.employee_id, 'job', jobId);
  };

  const loadDraftFromLocalStorage = () => {
    if (!selectedJobId || !user?.employee_id) {
      console.log('⚠️ Cannot load draft - missing selectedJobId or user.employee_id', { selectedJobId, employeeId: user?.employee_id });
      return;
    }

    const draftKey = `daily_report_draft_${user.employee_id}_${selectedJobId}`;
    console.log('🔍 Looking for draft with key:', draftKey, 'for user:', user.employee_id);
    const savedDraft = localStorage.getItem(draftKey);

    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);

        // Validate that draft belongs to current user (extra safety check)
        if (draft.employeeId && draft.employeeId !== user.employee_id) {
          console.log('⚠️ Draft belongs to different user, skipping. Draft user:', draft.employeeId, 'Current user:', user.employee_id);
          localStorage.removeItem(draftKey);
          return;
        }

        const draftDate = new Date(draft.timestamp);
        const now = new Date();
        const hoursSinceDraft = (now.getTime() - draftDate.getTime()) / (1000 * 60 * 60);

        // Only load draft if it's from today (within last 24 hours)
        if (hoursSinceDraft < 24) {
          console.log('📥 Loading draft from localStorage for user', user.employee_id, 'job', selectedJobId, 'draft employeeId:', draft.employeeId);
          setWorkDescription(draft.workDescription || '');
          setIssues(draft.issues || '');
          setSelectedEquipment(draft.selectedEquipment || []);
          setSelectedRentalEquipment(draft.selectedRentalEquipment || []);
          setEquipmentIssues(draft.equipmentIssues || []);
          setSiltFenceCondition(draft.siltFenceCondition || '');
          setSiltFenceRepairStatus(draft.siltFenceRepairStatus || 'none');
          setSiltFenceNotes(draft.siltFenceNotes || '');
          setSelectedJobNeeds(draft.selectedJobNeeds || []);
          setJobNeedsOtherText(draft.jobNeedsOtherText || '');
          // Manual truck ticket entry
          if (draft.manualTicketEntryEnabled !== undefined) setManualTicketEntryEnabled(draft.manualTicketEntryEnabled);
          if (draft.manualLoadsFill !== undefined) setManualLoadsFill(draft.manualLoadsFill);
          if (draft.manualLoadsRock !== undefined) setManualLoadsRock(draft.manualLoadsRock);
          if (draft.manualLoadsBase !== undefined) setManualLoadsBase(draft.manualLoadsBase);
          if (draft.manualLoadsAsphalt !== undefined) setManualLoadsAsphalt(draft.manualLoadsAsphalt);
          if (draft.manualLoadsOther !== undefined) setManualLoadsOther(draft.manualLoadsOther);
          if (draft.manualLoadsNotes !== undefined) setManualLoadsNotes(draft.manualLoadsNotes);
          // Loads hauled off
          if (draft.haulOffFillDirt !== undefined) setHaulOffFillDirt(draft.haulOffFillDirt);
          if (draft.haulOffBaserock !== undefined) setHaulOffBaserock(draft.haulOffBaserock);
          if (draft.haulOffAsphalt !== undefined) setHaulOffAsphalt(draft.haulOffAsphalt);
          if (draft.haulOffAsphaltMillings !== undefined) setHaulOffAsphaltMillings(draft.haulOffAsphaltMillings);
          if (draft.haulOffConcreteDump !== undefined) setHaulOffConcreteDump(draft.haulOffConcreteDump);
          if (draft.haulOffMiscDump !== undefined) setHaulOffMiscDump(draft.haulOffMiscDump);
          if (draft.haulOffConcreteDumpster !== undefined) setHaulOffConcreteDumpster(draft.haulOffConcreteDumpster);
          if (draft.haulOffDemolitionDumpster !== undefined) setHaulOffDemolitionDumpster(draft.haulOffDemolitionDumpster);
          if (draft.haulOffVegetationDumpster !== undefined) setHaulOffVegetationDumpster(draft.haulOffVegetationDumpster);
          if (draft.haulOffNotes !== undefined) setHaulOffNotes(draft.haulOffNotes);
          // Subcontractors
          if (draft.hadSubcontractors !== undefined) setHadSubcontractors(draft.hadSubcontractors);
          if (draft.selectedSubcontractors !== undefined) setSelectedSubcontractors(draft.selectedSubcontractors);
          if (draft.subcontractorNotes !== undefined) setSubcontractorNotes(draft.subcontractorNotes);
          if (draft.subcontractorWorkItems !== undefined) setSubcontractorWorkItems(draft.subcontractorWorkItems);
        } else {
          // Draft is old, remove it
          localStorage.removeItem(draftKey);
          console.log('🗑️ Removed old draft for user', user.employee_id, 'job', selectedJobId);
        }
      } catch (error) {
        console.error('Error loading draft:', error);
        localStorage.removeItem(draftKey);
      }
    }
  };

  const clearDraft = () => {
    if (selectedJobId && user?.employee_id) {
      localStorage.removeItem(`daily_report_draft_${user.employee_id}_${selectedJobId}`);
      console.log('🗑️ Cleared draft for user', user.employee_id, 'job', selectedJobId);
    }
  };

  // Load form data from a pending upload in the queue
  // This recovers data when a report was submitted but hasn't uploaded yet (e.g., due to low signal)
  const loadFromPendingUpload = async (jobId: number): Promise<boolean> => {
    if (!user?.employee_id) return false;

    try {
      const pendingUpload = await uploadQueueService.getPendingUpload('daily_report', user.employee_id, jobId);

      if (pendingUpload && pendingUpload.data) {
        console.log('📤 Found pending upload in queue, recovering data...');
        const data = pendingUpload.data;

        // Load the description and other fields from the queued data
        setWorkDescription(data.work_completed_description || '');
        setIssues(data.issues || '');

        // Parse equipment if it's a JSON string
        if (data.equipment) {
          try {
            const equipment = typeof data.equipment === 'string' ? JSON.parse(data.equipment) : data.equipment;
            setSelectedEquipment(equipment.map((e: any) => e.equipment_id) || []);
          } catch (e) {
            console.warn('Could not parse equipment from pending upload:', e);
          }
        }

        // Silt fence data
        setSiltFenceCondition(data.silt_fence_condition || '');
        setSiltFenceRepairStatus(data.silt_fence_repair_status || 'none');
        setSiltFenceNotes(data.silt_fence_notes || '');

        // Job needs
        if (data.job_needs) {
          try {
            const jobNeeds = typeof data.job_needs === 'string' ? JSON.parse(data.job_needs) : data.job_needs;
            setSelectedJobNeeds(jobNeeds.map((n: any) => n.item_id) || []);
          } catch (e) {
            console.warn('Could not parse job_needs from pending upload:', e);
          }
        }
        setJobNeedsOtherText(data.job_needs_other_text || '');

        // Manual ticket entry
        if (data.manual_ticket_entry_enabled !== undefined) setManualTicketEntryEnabled(data.manual_ticket_entry_enabled);
        if (data.manual_loads_fill !== undefined) setManualLoadsFill(data.manual_loads_fill);
        if (data.manual_loads_rock !== undefined) setManualLoadsRock(data.manual_loads_rock);
        if (data.manual_loads_base !== undefined) setManualLoadsBase(data.manual_loads_base);
        if (data.manual_loads_asphalt !== undefined) setManualLoadsAsphalt(data.manual_loads_asphalt);
        if (data.manual_loads_other !== undefined) setManualLoadsOther(data.manual_loads_other);
        if (data.manual_loads_notes !== undefined) setManualLoadsNotes(data.manual_loads_notes);

        // Loads hauled off
        if (data.hauled_off_loads) {
          try {
            const haulOff = typeof data.hauled_off_loads === 'string' ? JSON.parse(data.hauled_off_loads) : data.hauled_off_loads;
            if (Array.isArray(haulOff)) {
              haulOff.forEach((item: any) => {
                switch(item.material_type) {
                  case 'Fill Dirt': setHaulOffFillDirt(item.loads || 0); break;
                  case 'Base Rock': setHaulOffBaserock(item.loads || 0); break;
                  case 'Asphalt': setHaulOffAsphalt(item.loads || 0); break;
                  case 'Asphalt Millings': setHaulOffAsphaltMillings(item.loads || 0); break;
                  case 'Concrete (Dump Site)': setHaulOffConcreteDump(item.loads || 0); break;
                  case 'Misc (Dump Site)': setHaulOffMiscDump(item.loads || 0); break;
                  case 'Concrete (Dumpster)': setHaulOffConcreteDumpster(item.loads || 0); break;
                  case 'Demolition (Dumpster)': setHaulOffDemolitionDumpster(item.loads || 0); break;
                  case 'Vegetation (Dumpster)': setHaulOffVegetationDumpster(item.loads || 0); break;
                }
              });
            }
          } catch (e) {
            console.warn('Could not parse hauled_off_loads from pending upload:', e);
          }
        }
        if (data.haul_off_notes !== undefined) setHaulOffNotes(data.haul_off_notes);

        // Subcontractors
        if (data.subcontractors) {
          try {
            const subs = typeof data.subcontractors === 'string' ? JSON.parse(data.subcontractors) : data.subcontractors;
            if (Array.isArray(subs) && subs.length > 0) {
              setHadSubcontractors(true);
              setSelectedSubcontractors(subs.map((s: any) => s.type_id));
              const notes: Record<number, string> = {};
              subs.forEach((s: any) => {
                if (s.notes) notes[s.type_id] = s.notes;
              });
              setSubcontractorNotes(notes);
            }
          } catch (e) {
            console.warn('Could not parse subcontractors from pending upload:', e);
          }
        }

        console.log('✅ Data recovered from pending upload queue');
        setLoadedFromPendingUpload(true);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error checking for pending upload:', error);
      return false;
    }
  };

  // Track upload status and update UI
  const trackUploadStatus = async (uploadId: number) => {
    console.log(`📤 Tracking upload ${uploadId}...`);
    const startTime = Date.now();
    const timeoutMs = 120000; // 2 minute timeout

    const checkStatus = async () => {
      try {
        const status = await uploadQueueService.getUploadStatus(uploadId);

        if (!status || status.status === 'completed') {
          console.log(`✅ Upload ${uploadId} completed successfully!`);
          setUploadStatus('completed');
          return;
        }

        if (status.status === 'failed') {
          console.log(`❌ Upload ${uploadId} failed: ${status.error}`);
          setUploadStatus('failed');
          return;
        }

        // Check for timeout
        if (Date.now() - startTime > timeoutMs) {
          console.log(`⏱️ Upload ${uploadId} tracking timeout - will continue in background`);
          setUploadStatus('timeout');
          return;
        }

        // Still uploading, check again in 1 second
        setTimeout(checkStatus, 1000);
      } catch (err) {
        console.error('Error checking upload status:', err);
        // Keep checking on error
        setTimeout(checkStatus, 2000);
      }
    };

    checkStatus();
  };

  // AI Text Improvement
  const improveText = async (text: string, fieldName: string, setter: (val: string) => void, isForeman: boolean) => {
    if (!text.trim() || text.length < 3) return;

    // Don't improve if already improving
    if (aiImproving[fieldName]) return;

    // Remember the current step - don't apply changes if user navigated away
    const currentStepBeforeRequest = step;

    try {
      setAiImproving(prev => ({ ...prev, [fieldName]: true }));

      const response = await fetch(`${API_BASE_URL}/api/ai/improve-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        },
        body: JSON.stringify({ text })
      });

      const data = await response.json();

      // Only apply changes if user is still on the same step
      if (data.success && step === currentStepBeforeRequest) {
        const improved = data.data.improved;
        const wasSpanish = data.data.was_spanish;

        if (isForeman && wasSpanish) {
          // Foremen + Spanish text: Auto-translate immediately
          setter(improved);
          setAiIndicators(prev => ({
            ...prev,
            [fieldName]: 'translated'
          }));

          // Clear indicator after 3 seconds
          setTimeout(() => {
            setAiIndicators(prev => ({ ...prev, [fieldName]: null }));
          }, 3000);
        } else {
          // Foremen (English text) OR Admins: Show as suggestion
          setAiSuggestions(prev => ({ ...prev, [fieldName]: improved }));
          setAiIndicators(prev => ({
            ...prev,
            [fieldName]: wasSpanish ? 'translated' : 'improved'
          }));
        }
      }
    } catch (error) {
      console.error('AI improvement error:', error);
    } finally {
      setAiImproving(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const acceptSuggestion = (fieldName: string, setter: (val: string) => void) => {
    const suggestion = aiSuggestions[fieldName];
    if (suggestion) {
      setter(suggestion);
      setAiSuggestions(prev => ({ ...prev, [fieldName]: '' }));
      setAiIndicators(prev => ({ ...prev, [fieldName]: null }));
    }
  };

  const rejectSuggestion = (fieldName: string) => {
    setAiSuggestions(prev => ({ ...prev, [fieldName]: '' }));
    setAiIndicators(prev => ({ ...prev, [fieldName]: null }));
  };

  // Load shared manual counts from API (shared across all foremen/admins for this job/date)
  const loadSharedManualCounts = async () => {
    if (!selectedJobId) return;

    const todayDate = getTodayDateString();
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/manual-counts?job_id=${selectedJobId}&report_date=${todayDate}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
          }
        }
      );
      const result = await response.json();

      if (result.success && result.data) {
        // Load shared counts into state
        setManualLoadsFill(result.data.manual_loads_fill || 0);
        setManualLoadsRock(result.data.manual_loads_rock || 0);
        setManualLoadsBase(result.data.manual_loads_base || 0);
        setManualLoadsAsphalt(result.data.manual_loads_asphalt || 0);
        setManualLoadsOther(result.data.manual_loads_other || 0);
        setManualLoadsNotes(result.data.manual_loads_notes || '');
        console.log('✅ Loaded shared manual counts for job', selectedJobId, 'date', todayDate);
      }
    } catch (error) {
      console.error('❌ Failed to load shared manual counts:', error);
    }
  };

  // Save shared manual counts to API (debounced via useEffect)
  const saveSharedManualCounts = async () => {
    if (!selectedJobId) return;

    const todayDate = getTodayDateString();
    try {
      await fetch(`${API_BASE_URL}/api/manual-counts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        },
        body: JSON.stringify({
          job_id: selectedJobId,
          report_date: todayDate,
          manual_loads_fill: manualLoadsFill,
          manual_loads_rock: manualLoadsRock,
          manual_loads_base: manualLoadsBase,
          manual_loads_asphalt: manualLoadsAsphalt,
          manual_loads_other: manualLoadsOther,
          manual_loads_notes: manualLoadsNotes
        })
      });
      console.log('✅ Saved shared manual counts');
    } catch (error) {
      console.error('❌ Failed to save shared manual counts:', error);
    }
  };

  const loadJobs = async () => {
    try {
      console.log('📡 Loading jobs from API...');
      const response = await fetch(`${API_BASE_URL}/api/jobs`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const data = await response.json();
      console.log('📡 Jobs API response:', data);
      if (data.success) {
        // Backend already filters for active jobs by default (is_active = 1)
        // No client-side filtering needed - just use the data directly
        console.log('✅ Active jobs loaded:', data.data.length, data.data.map((j: any) => j.job_name));
        setJobs(data.data);
        return data.data; // Return jobs for immediate use
      }
      console.log('❌ Jobs API failed:', data);
      return [];
    } catch (err) {
      console.error('❌ Error loading jobs:', err);
      return [];
    }
  };

  const loadEquipment = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setAllEquipment(data.data);
      }
    } catch (err) {
      console.error('Error loading equipment:', err);
    }
  };

  const loadJobNeedsItems = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/job-needs/items?active=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setJobNeedsItems(data.data);
      }
    } catch (err) {
      console.error('Error loading job needs items:', err);
    }
  };

  const loadManualTicketEntrySetting = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/manual-ticket-entry`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const data = await response.json();
      console.log('🔍 DEBUG loadManualTicketEntrySetting:', { data, enabled: data.data?.enabled });
      if (data.success) {
        // Setting controls whether to show Step 2 (truck tickets)
        // OFF (default) = hide Step 2, skip to subcontractors
        // ON = show Step 2 with the question
        setShowTicketScanQuestion(data.data.enabled || false);
        console.log('🔍 DEBUG set showTicketScanQuestion to:', data.data.enabled || false);
      }
    } catch (err) {
      console.error('Error loading manual ticket entry setting:', err);
      setShowTicketScanQuestion(false); // Default to false (hide Step 2)
      console.log('🔍 DEBUG error - set showTicketScanQuestion to false');
    }
  };

  // Check if truck tickets have already been scanned for this job today
  const checkTicketsAlreadyScanned = async (jobId: number): Promise<boolean> => {
    try {
      const today = getTodayDateString();
      const response = await fetch(`${API_BASE_URL}/api/trucks?job_id=${jobId}&start_date=${today}&end_date=${today}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const data = await response.json();
      if (data.success && data.data && data.data.length > 0) {
        console.log(`🎫 Found ${data.data.length} truck tickets already scanned for job ${jobId} today`);
        setTicketsAlreadyScanned(true);
        return true;
      } else {
        console.log(`🎫 No truck tickets scanned yet for job ${jobId} today`);
        setTicketsAlreadyScanned(false);
        return false;
      }
    } catch (err) {
      console.error('Error checking tickets:', err);
      setTicketsAlreadyScanned(false);
      return false;
    }
  };

  const loadSubcontractorTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/subcontractors/types`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setAllSubcontractorTypes(data.data);
      }
    } catch (err) {
      console.error('Error loading subcontractor types:', err);
    }
  };

  const loadJobSubcontractors = async (jobId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/jobs/${jobId}/subcontractors`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setJobSubcontractors(data.data);

        // Merge all types with job-specific assignments
        const displayList = allSubcontractorTypes.map(type => {
          const assignment = data.data.find((ja: any) => ja.type_id === type.type_id);
          return {
            type_id: type.type_id,
            type_name: type.type_name,
            type_code: type.type_code,
            show_in_daily_reports: type.show_in_daily_reports,
            // Job-specific data (if assigned)
            job_subcontractor_id: assignment?.id || null,
            company: assignment?.subcontractor_company || null,
            contact: assignment?.contact_name || null,
            phone: assignment?.contact_phone || null
          };
        }).filter(sub => {
          // Filter out subcontractors based on job startup settings:
          // 1. Hide types not enabled for daily reports (controlled via admin portal)
          if (sub.show_in_daily_reports === false) return false;

          // 2. Hide if not assigned (no company, empty, or 'Not Required')
          if (!sub.company || sub.company === '' || sub.company === 'Not Required') return false;

          // 3. Hide 'By General Contractor/Owner' EXCEPT for testing & survey
          if (sub.company === 'By General Contractor/Owner') {
            // Keep testing and survey even if by owner
            if (sub.type_code === 'testing' || sub.type_code === 'survey') return true;
            return false;
          }

          return true;
        });
        setSubcontractorDisplayList(displayList);
      }
    } catch (err) {
      console.error('Error loading job subcontractors:', err);
    }
  };

  // Load work item templates for selected subcontractor types
  const loadWorkItemTemplatesForTypes = async (typeIds: number[]) => {
    if (typeIds.length === 0) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/subcontractors/work-items/by-types?type_ids=${typeIds.join(',')}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setWorkItemTemplates(data.data);
        // Initialize work items for any new types
        setSubcontractorWorkItems(prev => {
          const updated = { ...prev };
          typeIds.forEach(typeId => {
            if (!updated[typeId]) {
              updated[typeId] = {};
              const templates = data.data[typeId] || [];
              templates.forEach((t: any) => {
                updated[typeId][t.template_id] = [{ line_number: 1 }];
              });
            }
          });
          return updated;
        });
      }
    } catch (err) {
      console.error('Error loading work item templates:', err);
    }
  };

  const loadCurrentJob = async (jobsList: any[]) => {
    try {
      // Check if a job was passed via navigation (from Job Activity page)
      if (passedJobId) {
        const passedJob = jobsList.find((job: any) => job.job_id === passedJobId);
        if (passedJob) {
          console.log('[DailyReports] Job passed via navigation:', passedJob.job_name);
          // Set report loading BEFORE setting job to prevent race condition
          setLoadingStates(prev => ({ ...prev, report: true }));
          setSelectedJobId(passedJob.job_id);
          setSelectedJobName(passedJob.job_name);
          return;
        }
      }

      // Check if user is clocked in to a job
      const response = await fetch(`${API_BASE_URL}/api/time/current`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const data = await response.json();
      console.log('Current job response:', data);

      if (data.success && data.data?.job_id) {
        console.log('Setting job_id from time entry:', data.data.job_id);
        // Set report loading BEFORE setting job to prevent race condition
        setLoadingStates(prev => ({ ...prev, report: true }));
        setSelectedJobId(data.data.job_id);
        setSelectedJobName(data.data.job_name || '');
        // Note: Job mismatch detection is handled globally by GlobalMismatchChecker
        return; // Found job from time entry, we're done
      }

      // Not clocked in - try to detect job from GPS location
      console.log('Not clocked in - checking GPS location for auto-detection...');
      await tryAutoDetectJobFromLocation(jobsList);
    } catch (err) {
      console.error('Error loading current job:', err);
    }
  };

  // Helper to check if user is within a job's geofence
  const isWithinJobGeofence = (job: any, userLat: number, userLng: number): boolean => {
    // Honor "geofence off" — disabled fences must never drive auto-selection of
    // the daily report's job. Same rule as useLocationMismatch: if the admin
    // turned the fence off, GPS can't sensibly pick it as the report's job.
    if (job.geofence_enabled === false) return false;
    if (job.geofence_type === 'circle' && job.geofence_data?.radius) {
      const distance = calculateDistance(userLat, userLng, job.lat, job.lng);
      return distance <= job.geofence_data.radius;
    } else if (job.geofence_type === 'polygon' && job.geofence_data?.points) {
      // Simple polygon check using ray casting
      const points = job.geofence_data.points;
      let inside = false;
      for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
        const xi = points[i].lat, yi = points[i].lng;
        const xj = points[j].lat, yj = points[j].lng;
        const intersect = ((yi > userLng) !== (yj > userLng))
            && (userLat < (xj - xi) * (userLng - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
      }
      return inside;
    }
    // Default: 100 meter radius
    const distance = calculateDistance(userLat, userLng, job.lat, job.lng);
    return distance <= 100;
  };

  // Try to auto-detect job from GPS location (for admins not clocked in)
  const tryAutoDetectJobFromLocation = async (jobsList: any[]) => {
    console.log('📍 Attempting GPS-based job auto-detection...');

    // Force a fresh location
    const freshLocation = await forceRefreshLocation();

    if (!freshLocation) {
      console.log('📍 No location available for auto-detection');
      return;
    }

    console.log('📍 Fresh location:', freshLocation.latitude, freshLocation.longitude);

    // Find the closest job within geofence
    let closestJob: any = null;
    let closestDistance = Infinity;

    for (const job of jobsList) {
      if (!job.lat || !job.lng) continue;

      const distance = calculateDistance(freshLocation.latitude, freshLocation.longitude, job.lat, job.lng);
      const isInGeofence = isWithinJobGeofence(job, freshLocation.latitude, freshLocation.longitude);

      if (isInGeofence && distance < closestDistance) {
        closestJob = job;
        closestDistance = distance;
      }
    }

    if (closestJob) {
      console.log(`📍 Auto-detected job from location: ${closestJob.job_name} (${Math.round(closestDistance)}m away)`);
      // Set report loading BEFORE setting job to prevent race condition
      setLoadingStates(prev => ({ ...prev, report: true }));
      setSelectedJobId(closestJob.job_id);
      setSelectedJobName(closestJob.job_name);
      setLocationDetectedJob(true);
    } else {
      console.log('📍 Not at any job site - user will manually select job');
    }
  };

  // Helper function to calculate distance between two coordinates (in meters)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  const loadRentalEquipment = async () => {
    if (!selectedJobId) return;

    try {
      const todayStr = getReportDateString();

      const response = await fetch(`${API_BASE_URL}/api/daily-reports/job-data?job_id=${selectedJobId}&report_date=${todayStr}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const data = await response.json();
      if (data.success && data.data.rentalEquipment && data.data.rentalEquipment.length > 0) {
        setSelectedRentalEquipment(prev => {
          // Merge with existing, deduplicate
          const merged = [...prev];
          for (const rental of data.data.rentalEquipment) {
            const exists = merged.some(
              r => r.rental_company === rental.rental_company && r.rental_equipment_type === rental.rental_equipment_type
            );
            if (!exists) {
              merged.push({ rental_company: rental.rental_company, rental_equipment_type: rental.rental_equipment_type });
            }
          }
          return merged;
        });
        console.log(`📋 Loaded ${data.data.rentalEquipment.length} rental equipment from job-data`);
      }
    } catch (err) {
      console.error('Error loading rental equipment:', err);
    }
  };

  // Load the machines GPS saw running at this job today. Foremen no longer pick
  // company equipment — this list only feeds the equipment-issue picker so the
  // machines actually on site show first.
  const loadJobSiteEquipment = async () => {
    if (!selectedJobId) return;

    try {
      // PRIORITY 1: GPS — machines whose trackers ran inside this job's geofence today
      const gpsResponse = await fetch(`${API_BASE_URL}/api/jobs/${selectedJobId}/equipment-hours`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const gpsData = await gpsResponse.json();

      if (gpsData.success && gpsData.data?.equipment?.length > 0) {
        const equipIds = gpsData.data.equipment.map((eq: any) => eq.equipment_id);
        setSelectedEquipment(equipIds);
        console.log(`📋 Loaded ${equipIds.length} equipment from GPS (ran at job site today)`);
        return;
      }

      // PRIORITY 2: Equipment currently checked out to the job (trucker tracking)
      const jobEquipmentResponse = await fetch(`${API_BASE_URL}/api/equipment/job/${selectedJobId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const jobEquipmentData = await jobEquipmentResponse.json();

      if (jobEquipmentData.success && jobEquipmentData.data.length > 0) {
        const equipIds = jobEquipmentData.data.map((eq: any) => eq.equipment_id);
        setSelectedEquipment(equipIds);
        console.log(`📋 Loaded ${equipIds.length} equipment from trucker tracking (currently at job site)`);
      }
    } catch (err) {
      console.error('Error loading job site equipment:', err);
    }
  };

  const loadTodaysReport = async () => {
    if (!selectedJobId || !user) {
      setLoadingStates(prev => ({ ...prev, report: false }));
      return;
    }

    // CRITICAL: Ensure user.employee_id is valid to prevent loading wrong user's data
    if (!user.employee_id || typeof user.employee_id !== 'number') {
      console.error('❌ CRITICAL: Invalid user.employee_id, aborting loadTodaysReport', user);
      setLoadingStates(prev => ({ ...prev, report: false }));
      return;
    }

    try {
      // Get report date in EST timezone (night work window shifts date forward)
      const todayStr = getReportDateString();
      const draftKey = `daily_report_draft_${user.employee_id}_${selectedJobId}`;
      console.log('🔑 Draft key for current user:', draftKey);

      // PRIORITY 1: Check database for SUBMITTED report FIRST (cross-device editing)
      // This ensures reports submitted on phone/iPad can be edited on any device
      console.log(`📡 Checking database for submitted report: job_id=${selectedJobId}, date=${todayStr}, current_user_id=${user.employee_id}`);

      const response = await fetch(`${API_BASE_URL}/api/daily-reports?job_id=${selectedJobId}&date=${todayStr}&for_user=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const data = await response.json();

        console.log('Daily reports API response:', data);
        console.log('📋 Reports returned from API:', data.data?.map((r: any) => ({ report_id: r.report_id, foreman_id: r.foreman_id, foreman_name: `${r.foreman_first_name} ${r.foreman_last_name}` })));
        console.log('📋 Current user employee_id:', user.employee_id);

        if (data.success && data.data.length > 0) {
          console.log(`Found ${data.data.length} reports for today, looking for foreman_id=${user.employee_id}`);
          // Find the report submitted by the current user
          const myReport = data.data.find((r: any) => r.foreman_id === user.employee_id);
          console.log('📋 myReport found:', myReport ? `Yes - report_id ${myReport.report_id}` : 'No');

          if (myReport) {
            console.log('✅ Found submitted report in database:', {
              report_id: myReport.report_id,
              report_date: myReport.report_date,
              work_description: myReport.work_completed_description?.substring(0, 50),
              hauled_off_loads: myReport.hauled_off_loads,
              subcontractors: myReport.subcontractors,
              manual_ticket_entry_enabled: myReport.manual_ticket_entry_enabled,
              haul_off_notes: myReport.haul_off_notes
            });
            setExistingReportId(myReport.report_id);
            console.log('🔍 DEBUG: Set existingReportId to:', myReport.report_id);

            // Pre-fill form fields from database
            // Night-aware: load the correct description/issues based on current time
            const isNight = getNightWorkStatus();
            const workDesc = isNight
              ? (myReport.night_work_description || '')
              : (myReport.work_completed_description || '');
            const isPhotoOnly = workDesc.toLowerCase().includes('photo') && workDesc.toLowerCase().includes('upload') && workDesc.toLowerCase().includes('only');
            setWorkDescription(isPhotoOnly ? '' : workDesc);
            setIssues(isNight ? (myReport.night_issues || '') : (myReport.issues || ''));

            // Load equipment - if none, preload from previous days
            if (myReport.equipment && myReport.equipment.length > 0) {
              const companyEquipIds = myReport.equipment
                .filter((eq: any) => !eq.is_rental && eq.equipment_id)
                .map((eq: any) => eq.equipment_id);
              setSelectedEquipment(companyEquipIds);

              // Load rental equipment from the existing report
              const rentalItems = myReport.equipment
                .filter((eq: any) => eq.is_rental && eq.rental_company && eq.rental_equipment_type)
                .map((eq: any) => ({ rental_company: eq.rental_company, rental_equipment_type: eq.rental_equipment_type }));
              if (rentalItems.length > 0) {
                setSelectedRentalEquipment(prev => {
                  const merged = [...prev];
                  for (const rental of rentalItems) {
                    const exists = merged.some(
                      r => r.rental_company === rental.rental_company && r.rental_equipment_type === rental.rental_equipment_type
                    );
                    if (!exists) merged.push(rental);
                  }
                  return merged;
                });
                console.log(`📋 Loaded ${rentalItems.length} rental equipment from existing report`);
              }
            } else {
              console.log('📋 Report has no equipment, preloading from previous days...');
              loadJobSiteEquipment();
            }

            // Load job needs
            if (myReport.job_needs) {
              try {
                const parsedNeeds = typeof myReport.job_needs === 'string'
                  ? JSON.parse(myReport.job_needs)
                  : myReport.job_needs;
                if (Array.isArray(parsedNeeds)) {
                  const needIds = parsedNeeds.map((n: any) => n.item_id);
                  setSelectedJobNeeds(needIds);
                  console.log('📦 Loaded job needs:', needIds);
                }
              } catch (e) {
                console.error('Error parsing job_needs:', e);
              }
            }
            if (myReport.job_needs_other_text) {
              setJobNeedsOtherText(myReport.job_needs_other_text);
            }

            // Load silt fence data
            if (myReport.silt_fence_condition) {
              setSiltFenceCondition(myReport.silt_fence_condition);
            }
            if (myReport.silt_fence_repair_status) {
              setSiltFenceRepairStatus(myReport.silt_fence_repair_status);
            }
            if (myReport.silt_fence_notes) {
              setSiltFenceNotes(myReport.silt_fence_notes);
            }

            // Load existing photos
            if (myReport.photo_urls) {
              try {
                console.log('📸 DEBUG: Raw photo_urls from database:', myReport.photo_urls);
                console.log('📸 DEBUG: Type of photo_urls:', typeof myReport.photo_urls);

                // Backend returns JSON.stringify(photoData) where photoData is array of objects or strings
                const photoData = typeof myReport.photo_urls === 'string'
                  ? JSON.parse(myReport.photo_urls)
                  : myReport.photo_urls;

                console.log('📸 DEBUG: Parsed photo_urls:', photoData);
                console.log('📸 DEBUG: Is array?', Array.isArray(photoData));

                if (Array.isArray(photoData) && photoData.length > 0) {
                  // Extract thumbnail URLs (or fall back to full URLs for backwards compatibility)
                  const photoUrls = photoData.map((p: any) => {
                    if (typeof p === 'string') return p; // Backwards compatibility for old string format
                    // Backend sends {photoUrl, thumbnailUrl} - use thumbnail for display
                    return p.thumbnailUrl || p.thumbnail_url || p.photoUrl || p.photo_url || p;
                  });
                  setExistingDailyPhotos(photoUrls);
                  console.log('✅ Loaded existing daily photos (thumbnails):', photoUrls.length);
                  console.log('📸 DEBUG: Sample thumbnail URL:', photoUrls[0]);
                }
              } catch (e) {
                console.error('❌ Error parsing photo_urls:', e);
              }
            }

            if (myReport.silt_fence_photos) {
              try {
                console.log('📸 DEBUG: Raw silt_fence_photos from database:', myReport.silt_fence_photos);
                console.log('📸 DEBUG: Type of silt_fence_photos:', typeof myReport.silt_fence_photos);

                // Backend returns JSON.stringify(array of {photoUrl, thumbnailUrl})
                const siltPhotoData = typeof myReport.silt_fence_photos === 'string'
                  ? JSON.parse(myReport.silt_fence_photos)
                  : myReport.silt_fence_photos;

                console.log('📸 DEBUG: Parsed silt_fence_photos:', siltPhotoData);

                if (Array.isArray(siltPhotoData) && siltPhotoData.length > 0) {
                  // Extract thumbnailUrl from objects (use thumbnails for display, full photo on click)
                  const siltPhotoUrls = siltPhotoData.map((p: any) => {
                    if (typeof p === 'string') return p;
                    // Backend sends {photoUrl, thumbnailUrl} - use thumbnail for display
                    return p.thumbnailUrl || p.thumbnail_url || p.photoUrl || p.photo_url;
                  });
                  setExistingSiltFencePhotos(siltPhotoUrls);
                  console.log('✅ Loaded existing silt fence photos (thumbnails):', siltPhotoUrls.length);
                  console.log('📸 DEBUG: Sample thumbnail URL:', siltPhotoUrls[0]);
                }
              } catch (e) {
                console.error('❌ Error parsing silt_fence_photos:', e);
              }
            }

            // Load manual truck ticket entry data
            // DON'T set manualTicketEntryEnabled yet - always show question first when editing
            // User can click "No - Manual Entry" to see/edit the saved manual ticket counts
            // if (myReport.manual_ticket_entry_enabled !== undefined) {
            //   setManualTicketEntryEnabled(myReport.manual_ticket_entry_enabled);
            // }

            // Load the manual ticket counts so they're ready if user chooses manual entry
            if (myReport.manual_loads_fill !== undefined) {
              setManualLoadsFill(myReport.manual_loads_fill);
            }
            if (myReport.manual_loads_rock !== undefined) {
              setManualLoadsRock(myReport.manual_loads_rock);
            }
            if (myReport.manual_loads_base !== undefined) {
              setManualLoadsBase(myReport.manual_loads_base);
            }
            if (myReport.manual_loads_asphalt !== undefined) {
              setManualLoadsAsphalt(myReport.manual_loads_asphalt);
            }
            if (myReport.manual_loads_other !== undefined) {
              setManualLoadsOther(myReport.manual_loads_other);
            }
            if (myReport.manual_loads_notes) {
              setManualLoadsNotes(myReport.manual_loads_notes);
            }
            console.log('📋 Loaded manual ticket counts but keeping form disabled - will show question first');

            // Load loads hauled off site data
            console.log('🔍 DEBUG: myReport.hauled_off_loads RAW:', myReport.hauled_off_loads);
            if (myReport.hauled_off_loads) {
              try {
                const haulOffData = typeof myReport.hauled_off_loads === 'string'
                  ? JSON.parse(myReport.hauled_off_loads)
                  : myReport.hauled_off_loads;

                console.log('🔍 DEBUG: haulOffData PARSED:', haulOffData);
                console.log('🔍 DEBUG: Is array?', Array.isArray(haulOffData));

                if (Array.isArray(haulOffData)) {
                  haulOffData.forEach((load: any) => {
                    const { load_type, material_type, load_count } = load;
                    console.log(`🔍 DEBUG: Loading ${material_type} (${load_type}): ${load_count}`);
                    if (load_type === 'dump_truck') {
                      if (material_type === 'fill_dirt') setHaulOffFillDirt(load_count);
                      else if (material_type === 'baserock') setHaulOffBaserock(load_count);
                      else if (material_type === 'asphalt') setHaulOffAsphalt(load_count);
                      else if (material_type === 'asphalt_millings') setHaulOffAsphaltMillings(load_count);
                      else if (material_type === 'concrete') setHaulOffConcreteDump(load_count);
                      else if (material_type === 'misc') setHaulOffMiscDump(load_count);
                    } else if (load_type === 'dumpster') {
                      if (material_type === 'concrete') setHaulOffConcreteDumpster(load_count);
                      else if (material_type === 'demolition_debris') setHaulOffDemolitionDumpster(load_count);
                      else if (material_type === 'vegetation') setHaulOffVegetationDumpster(load_count);
                    }
                  });
                  console.log('✅ Loaded loads hauled off data');
                }
              } catch (e) {
                console.error('❌ Error parsing hauled_off_loads:', e);
              }
            } else {
              console.log('⚠️ No hauled_off_loads in myReport');
            }
            if (myReport.haul_off_notes) {
              setHaulOffNotes(myReport.haul_off_notes);
            }

            // Load subcontractors data
            console.log('🔍 DEBUG: myReport.subcontractors RAW:', myReport.subcontractors);
            if (myReport.subcontractors) {
              try {
                const subData = typeof myReport.subcontractors === 'string'
                  ? JSON.parse(myReport.subcontractors)
                  : myReport.subcontractors;

                console.log('🔍 DEBUG: subData PARSED:', subData);
                console.log('🔍 DEBUG: Is array?', Array.isArray(subData), 'Length:', subData?.length);

                if (Array.isArray(subData) && subData.length > 0) {
                  setHadSubcontractors(true);
                  const subIds = subData.map((s: any) => s.type_id);
                  setSelectedSubcontractors(subIds);
                  console.log('🔍 DEBUG: Setting selectedSubcontractors to:', subIds);

                  // Load notes for each subcontractor
                  const notes: {[key: number]: string} = {};
                  subData.forEach((s: any) => {
                    if (s.notes) {
                      notes[s.type_id] = s.notes;
                    }
                  });
                  setSubcontractorNotes(notes);
                  console.log('✅ Loaded subcontractors data:', subIds.length);
                } else {
                  console.log('⚠️ subData is not array or empty');
                  setHadSubcontractors(false);
                }
              } catch (e) {
                console.error('❌ Error parsing subcontractors:', e);
              }
            } else {
              console.log('⚠️ No subcontractors in myReport');
            }

            // Submitted report found - clear any stale localStorage draft
            if (localStorage.getItem(draftKey)) {
              localStorage.removeItem(draftKey);
              console.log('🗑️ Cleared localStorage draft (using submitted report instead)');
            }

            // Continue to load equipment issues below, then return
          } else {
            // No existing report for this user today - check pending upload queue first, then localStorage draft
            console.log('📋 No submitted report found, checking pending upload queue...');

            // First check if there's a pending upload in the queue (report submitted but not yet uploaded due to low signal)
            const loadedFromQueue = await loadFromPendingUpload(selectedJobId);
            if (loadedFromQueue) {
              console.log('✅ Loaded data from pending upload queue');
              return; // Don't load from draft or previous equipment
            }

            console.log('📋 No pending upload, checking localStorage draft...');
            const savedDraft = localStorage.getItem(draftKey);

            if (savedDraft) {
              try {
                const draft = JSON.parse(savedDraft);
                const draftDate = new Date(draft.timestamp);
                const now = new Date();
                const hoursSinceDraft = (now.getTime() - draftDate.getTime()) / (1000 * 60 * 60);

                if (hoursSinceDraft < 24) {
                  console.log('💾 Found localStorage draft, loading work-in-progress...');
                  setWorkDescription(draft.workDescription || '');
                  setIssues(draft.issues || '');
                  setSelectedEquipment(draft.selectedEquipment || []);

                  const draftIssues = draft.equipmentIssues || [];
                  console.log('🔧 DEBUG: Loading equipment issues from localStorage:', draftIssues.length);
                  setEquipmentIssues(draftIssues);

                  setSiltFenceCondition(draft.siltFenceCondition || '');
                  setSiltFenceRepairStatus(draft.siltFenceRepairStatus || 'none');
                  setSiltFenceNotes(draft.siltFenceNotes || '');
                  setSelectedJobNeeds(draft.selectedJobNeeds || []);
                  setJobNeedsOtherText(draft.jobNeedsOtherText || '');
                  // Load manual ticket entry data
                  if (draft.manualTicketEntryEnabled !== undefined) setManualTicketEntryEnabled(draft.manualTicketEntryEnabled);
                  if (draft.manualLoadsFill !== undefined) setManualLoadsFill(draft.manualLoadsFill);
                  if (draft.manualLoadsRock !== undefined) setManualLoadsRock(draft.manualLoadsRock);
                  if (draft.manualLoadsBase !== undefined) setManualLoadsBase(draft.manualLoadsBase);
                  if (draft.manualLoadsAsphalt !== undefined) setManualLoadsAsphalt(draft.manualLoadsAsphalt);
                  if (draft.manualLoadsOther !== undefined) setManualLoadsOther(draft.manualLoadsOther);
                  if (draft.manualLoadsNotes !== undefined) setManualLoadsNotes(draft.manualLoadsNotes);
                  // Load loads hauled off data
                  if (draft.haulOffFillDirt !== undefined) setHaulOffFillDirt(draft.haulOffFillDirt);
                  if (draft.haulOffBaserock !== undefined) setHaulOffBaserock(draft.haulOffBaserock);
                  if (draft.haulOffAsphalt !== undefined) setHaulOffAsphalt(draft.haulOffAsphalt);
                  if (draft.haulOffAsphaltMillings !== undefined) setHaulOffAsphaltMillings(draft.haulOffAsphaltMillings);
                  if (draft.haulOffConcreteDump !== undefined) setHaulOffConcreteDump(draft.haulOffConcreteDump);
                  if (draft.haulOffMiscDump !== undefined) setHaulOffMiscDump(draft.haulOffMiscDump);
                  if (draft.haulOffConcreteDumpster !== undefined) setHaulOffConcreteDumpster(draft.haulOffConcreteDumpster);
                  if (draft.haulOffDemolitionDumpster !== undefined) setHaulOffDemolitionDumpster(draft.haulOffDemolitionDumpster);
                  if (draft.haulOffVegetationDumpster !== undefined) setHaulOffVegetationDumpster(draft.haulOffVegetationDumpster);
                  if (draft.haulOffNotes !== undefined) setHaulOffNotes(draft.haulOffNotes);
                  // Load subcontractors data
                  if (draft.hadSubcontractors !== undefined) setHadSubcontractors(draft.hadSubcontractors);
                  if (draft.selectedSubcontractors !== undefined) setSelectedSubcontractors(draft.selectedSubcontractors);
                  if (draft.subcontractorNotes !== undefined) setSubcontractorNotes(draft.subcontractorNotes);
                  if (draft.subcontractorWorkItems !== undefined) setSubcontractorWorkItems(draft.subcontractorWorkItems);
                  console.log('✅ Draft loaded from localStorage');
                  return; // Don't load equipment issues or previous equipment
                } else {
                  localStorage.removeItem(draftKey);
                  console.log('🗑️ Removed old draft');
                }
              } catch (error) {
                console.error('Error loading draft:', error);
                localStorage.removeItem(draftKey);
              }
            }

            // No draft either - load previous equipment as default (up to 5 days back)
            console.log('📋 No draft found, loading previous equipment as default');
            loadJobSiteEquipment();
          }
        } else {
          // No reports found for today - check pending upload queue first, then localStorage draft
          console.log('📋 No reports found for today, checking pending upload queue...');

          // First check if there's a pending upload in the queue (report submitted but not yet uploaded due to low signal)
          const loadedFromQueue = await loadFromPendingUpload(selectedJobId);
          if (loadedFromQueue) {
            console.log('✅ Loaded data from pending upload queue');
            return; // Don't load from draft or previous equipment
          }

          console.log('📋 No pending upload, checking localStorage draft...');
          const savedDraft = localStorage.getItem(draftKey);

          if (savedDraft) {
            try {
              const draft = JSON.parse(savedDraft);
              const draftDate = new Date(draft.timestamp);
              const now = new Date();
              const hoursSinceDraft = (now.getTime() - draftDate.getTime()) / (1000 * 60 * 60);

              if (hoursSinceDraft < 24) {
                console.log('💾 Found localStorage draft, loading work-in-progress...');
                setWorkDescription(draft.workDescription || '');
                setIssues(draft.issues || '');
                setSelectedEquipment(draft.selectedEquipment || []);

                const draftIssues = draft.equipmentIssues || [];
                console.log('🔧 DEBUG: Loading equipment issues from localStorage:', draftIssues.length);
                setEquipmentIssues(draftIssues);

                setSiltFenceCondition(draft.siltFenceCondition || '');
                setSiltFenceRepairStatus(draft.siltFenceRepairStatus || 'none');
                setSiltFenceNotes(draft.siltFenceNotes || '');
                setSelectedJobNeeds(draft.selectedJobNeeds || []);
                setJobNeedsOtherText(draft.jobNeedsOtherText || '');
                // Load manual ticket entry data
                if (draft.manualTicketEntryEnabled !== undefined) setManualTicketEntryEnabled(draft.manualTicketEntryEnabled);
                if (draft.manualLoadsFill !== undefined) setManualLoadsFill(draft.manualLoadsFill);
                if (draft.manualLoadsRock !== undefined) setManualLoadsRock(draft.manualLoadsRock);
                if (draft.manualLoadsBase !== undefined) setManualLoadsBase(draft.manualLoadsBase);
                if (draft.manualLoadsAsphalt !== undefined) setManualLoadsAsphalt(draft.manualLoadsAsphalt);
                if (draft.manualLoadsOther !== undefined) setManualLoadsOther(draft.manualLoadsOther);
                if (draft.manualLoadsNotes !== undefined) setManualLoadsNotes(draft.manualLoadsNotes);
                // Load loads hauled off data
                if (draft.haulOffFillDirt !== undefined) setHaulOffFillDirt(draft.haulOffFillDirt);
                if (draft.haulOffBaserock !== undefined) setHaulOffBaserock(draft.haulOffBaserock);
                if (draft.haulOffAsphalt !== undefined) setHaulOffAsphalt(draft.haulOffAsphalt);
                if (draft.haulOffAsphaltMillings !== undefined) setHaulOffAsphaltMillings(draft.haulOffAsphaltMillings);
                if (draft.haulOffConcreteDump !== undefined) setHaulOffConcreteDump(draft.haulOffConcreteDump);
                if (draft.haulOffMiscDump !== undefined) setHaulOffMiscDump(draft.haulOffMiscDump);
                if (draft.haulOffConcreteDumpster !== undefined) setHaulOffConcreteDumpster(draft.haulOffConcreteDumpster);
                if (draft.haulOffDemolitionDumpster !== undefined) setHaulOffDemolitionDumpster(draft.haulOffDemolitionDumpster);
                if (draft.haulOffVegetationDumpster !== undefined) setHaulOffVegetationDumpster(draft.haulOffVegetationDumpster);
                if (draft.haulOffNotes !== undefined) setHaulOffNotes(draft.haulOffNotes);
                // Load subcontractors data
                if (draft.hadSubcontractors !== undefined) setHadSubcontractors(draft.hadSubcontractors);
                if (draft.selectedSubcontractors !== undefined) setSelectedSubcontractors(draft.selectedSubcontractors);
                if (draft.subcontractorNotes !== undefined) setSubcontractorNotes(draft.subcontractorNotes);
                if (draft.subcontractorWorkItems !== undefined) setSubcontractorWorkItems(draft.subcontractorWorkItems);
                console.log('✅ Draft loaded from localStorage');
                return; // Don't load equipment issues or previous equipment
              } else {
                localStorage.removeItem(draftKey);
                console.log('🗑️ Removed old draft');
              }
            } catch (error) {
              console.error('Error loading draft:', error);
              localStorage.removeItem(draftKey);
            }
          }

          // No draft either - load previous equipment as default (up to 5 days back)
          console.log('📋 No draft found, loading previous equipment as default');
          loadJobSiteEquipment();
        }

      // Load standalone equipment issues for this job/date (regardless of who submitted them)
      // This is needed when viewing the page to see all issues, even if no report exists yet
      try {
        const issuesResponse = await fetch(`${API_BASE_URL}/api/equipment/issues?status=open`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
          }
        });
        const issuesData = await issuesResponse.json();

        if (issuesData.success && issuesData.data.length > 0) {
          // Filter issues for this job and today's date
          const todayIssues = issuesData.data.filter((issue: any) => {
            if (issue.job_id !== selectedJobId) return false;

            // Check if issue was reported today
            // issue.reported_date comes from database in UTC, convert to local date for comparison
            const reportedDate = new Date(issue.reported_date);
            const reportedDateStr = `${reportedDate.getFullYear()}-${String(reportedDate.getMonth() + 1).padStart(2, '0')}-${String(reportedDate.getDate()).padStart(2, '0')}`;

            return reportedDateStr === todayStr;
          });

          // Only add these if they weren't already loaded from the report
          if (todayIssues.length > 0) {
            console.log(`📋 Found ${todayIssues.length} equipment issues from API for today`);

            setEquipmentIssues(prevIssues => {
              // Get existing issue IDs to avoid duplicates
              const existingIssueIds = new Set(prevIssues.map(i => i.issue_id).filter(id => id !== undefined));

              console.log(`📋 Duplicate detection - prevIssues count: ${prevIssues.length}, existing IDs:`, Array.from(existingIssueIds));
              console.log(`📋 API returned ${todayIssues.length} issues with IDs:`, todayIssues.map((i: any) => i.issue_id));

              const standaloneIssues = todayIssues
                .filter((issue: any) => {
                  const isDuplicate = existingIssueIds.has(issue.issue_id);
                  if (isDuplicate) {
                    console.log(`📋 Skipping duplicate issue ${issue.issue_id}: ${issue.issue_description}`);
                  }
                  return !isDuplicate;
                })
                .map((issue: any) => ({
                  issue_id: issue.issue_id, // Mark as existing issue
                  equipment_id: issue.equipment_id,
                  equipment_name: issue.equipment_name,
                  description: issue.issue_description,
                  severity: (issue.severity === 'critical' ? 'high' : issue.severity === 'moderate' ? 'medium' : 'low') as 'low' | 'medium' | 'high',
                  photos: [] // Photos already uploaded
                }));

              if (standaloneIssues.length > 0) {
                console.log(`📋 Adding ${standaloneIssues.length} new issues from API (${existingIssueIds.size} already loaded from report)`);
                return [...prevIssues, ...standaloneIssues];
              }

              console.log(`📋 No new issues to add (all ${todayIssues.length} were duplicates)`);
              return prevIssues;
            });
          }
        }
      } catch (issuesErr) {
        console.error('Error loading standalone equipment issues:', issuesErr);
      }
    } catch (err) {
      console.error('Error loading todays report:', err);
      // Fallback to previous equipment (up to 5 days back)
      loadJobSiteEquipment();
    } finally {
      // Mark report loading as complete so user can interact with form
      setLoadingStates(prev => ({ ...prev, report: false }));
    }
  };

  const compressImage = async (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;

          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  // Upload photo in background as soon as it's added
  const uploadPhotoInBackground = async (file: File, photoType: 'daily' | 'silt_fence' = 'daily', retryCount = 0) => {
    const previewUrl = URL.createObjectURL(file);
    const photoEntry: PhotoWithUpload = {
      file,
      previewUrl,
      uploadStatus: 'uploading'
    };

    // Add to tracking array immediately with 'uploading' status (only on first attempt)
    if (retryCount === 0) {
      setPhotoUploads(prev => [...prev, photoEntry]);
    }

    const MAX_RETRIES = 2;
    const TIMEOUT_MS = 60000; // 60 second timeout per attempt

    try {
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('photo_type', photoType);

      console.log(`📤 Starting background upload: ${file.name} (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`);

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

      const response = await fetch(`${API_BASE_URL}/api/daily-reports/upload-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        },
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (data.success) {
        console.log(`✅ Background upload complete: ${file.name} -> ${data.data.fullUrl}`);
        // Update the entry with uploaded URLs
        setPhotoUploads(prev => prev.map(p =>
          p.previewUrl === previewUrl
            ? { ...p, uploadStatus: 'uploaded' as const, uploadedUrl: data.data.fullUrl, thumbnailUrl: data.data.thumbnailUrl }
            : p
        ));

        // PERSIST to localStorage as backup (iOS Safari can lose React state)
        try {
          const storageKey = `preupload_${selectedJobId}_${photoType}`;
          const existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
          existing.push({
            filename: file.name,
            fullUrl: data.data.fullUrl,
            thumbnailUrl: data.data.thumbnailUrl,
            uploadedAt: new Date().toISOString()
          });
          localStorage.setItem(storageKey, JSON.stringify(existing));
          console.log(`💾 Persisted pre-upload URL to localStorage: ${file.name}`);
        } catch (e) {
          console.warn('Failed to persist pre-upload URL to localStorage:', e);
        }
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err: any) {
      console.error(`❌ Background upload error: ${file.name}`, err.message || err);

      // Retry if we haven't exceeded max retries
      if (retryCount < MAX_RETRIES) {
        console.log(`🔄 Retrying upload for ${file.name} in 2 seconds...`);
        setTimeout(() => {
          uploadPhotoInBackground(file, photoType, retryCount + 1);
        }, 2000);
      } else {
        // Mark as pending (will be uploaded via queue at submit time)
        console.log(`⏸️ Pre-upload failed for ${file.name}, will upload via queue at submit`);
        setPhotoUploads(prev => prev.map(p =>
          p.previewUrl === previewUrl
            ? { ...p, uploadStatus: 'pending' as const }
            : p
        ));
      }
    }
  };

  const handleTakePhoto = async () => {
    if (cameraCooldown) return;

    const MAX_PHOTOS = 20;

    if (photos.length >= MAX_PHOTOS) {
      setError(`Maximum ${MAX_PHOTOS} photos allowed.`);
      return;
    }

    if (isIOS()) {
      // iOS: Use native camera
      try {
        setCameraCooldown(true);

        await openNativeCamera((file) => {
          if (file && file instanceof File) {
            console.log('📸 Photo captured:', file.name, 'Size:', file.size, 'bytes');

            // Double-check limit before adding (in case user took multiple photos quickly)
            setPhotos(prev => {
              if (prev.length >= MAX_PHOTOS) {
                setError(`Maximum ${MAX_PHOTOS} photos allowed.`);
                return prev;
              }
              const updated = [...prev, file];
              console.log('📸 Photos array updated, new count:', updated.length);
              return updated;
            });
            setPhotoPreviews(prev => {
              if (prev.length >= MAX_PHOTOS) {
                return prev;
              }
              const previewUrl = URL.createObjectURL(file);
              const updated = [...prev, previewUrl];
              console.log('📸 Photo previews updated, new count:', updated.length);
              return updated;
            });

            // Start background upload immediately
            uploadPhotoInBackground(file, 'daily');
          }
        });

        setTimeout(() => setCameraCooldown(false), 1000);
      } catch (err) {
        console.error('Error capturing photo:', err);
        setError('Failed to capture photo. Please try again.');
        setCameraCooldown(false);
      }
    } else {
      // Android: Use custom camera component
      setCurrentCameraType('daily');
      setShowCamera(true);
    }
  };

  const removePhoto = (index: number) => {
    // Revoke object URL to free memory
    const urlToRevoke = photoPreviews[index];
    if (urlToRevoke && urlToRevoke.startsWith('blob:')) {
      URL.revokeObjectURL(urlToRevoke);
    }

    // Also remove from photoUploads tracking
    const fileToRemove = photos[index];
    setPhotoUploads(prev => prev.filter(p => p.file !== fileToRemove));

    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleTakeIssuePhoto = async () => {
    if (issueCameraCooldown) return;

    if (isIOS()) {
      // iOS: Use native camera
      try {
        setIssueCameraCooldown(true);

        await openNativeCamera((file) => {
          if (file && file instanceof File) {
            // Generate preview URL using object URL (memory-efficient vs base64)
            const previewUrl = URL.createObjectURL(file);

            // Add to existing photos array
            setCurrentIssue(prev => ({ ...prev, photos: [...prev.photos, file] }));
            setCurrentIssuePhotoPreviews(prev => [...prev, previewUrl]);
          }
        });

        setTimeout(() => setIssueCameraCooldown(false), 1000);
      } catch (err) {
        console.error('Error capturing photo:', err);
        setError('Failed to capture photo. Please try again.');
        setIssueCameraCooldown(false);
      }
    } else {
      // Android: Use custom camera component
      setCurrentCameraType('issue');
      setShowCamera(true);
    }
  };

  const removeIssuePhoto = (index: number) => {
    setCurrentIssue(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
    setCurrentIssuePhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const saveEquipmentIssue = () => {
    if (!currentIssue.equipment_id || !currentIssue.description.trim()) {
      alert('Please select equipment and describe the issue');
      return;
    }

    const equipment = allEquipment.find(e => e.equipment_id === currentIssue.equipment_id);
    if (!equipment) return;

    setEquipmentIssues(prev => [...prev, {
      equipment_id: currentIssue.equipment_id!,
      equipment_name: equipment.equipment_name,
      description: currentIssue.description,
      severity: currentIssue.severity,
      photos: currentIssue.photos.length > 0 ? currentIssue.photos : undefined
    }]);

    // Reset form
    setCurrentIssue({
      equipment_id: null,
      description: '',
      severity: 'medium',
      photos: []
    });
    setCurrentIssuePhotoPreviews([]);
    setShowIssueForm(false);
  };

  // Silt Fence Photo Handlers
  const handleTakeSiltFencePhoto = async () => {
    if (siltFenceCameraCooldown) return;

    if (isIOS()) {
      // iOS: Use native camera
      try {
        setSiltFenceCameraCooldown(true);

        await openNativeCamera(async (file) => {
          if (file && file instanceof File) {
            console.log('🌿 Silt fence photo captured:', file.name, 'Size:', file.size, 'bytes');

            // Generate preview URL using object URL (memory-efficient vs base64)
            const previewUrl = URL.createObjectURL(file);

            setSiltFencePhotos(prev => {
              const updated = [...prev, file];
              console.log('🌿 Silt fence photos array updated, new count:', updated.length);
              return updated;
            });
            setSiltFencePhotoPreviews(prev => {
              const updated = [...prev, previewUrl];
              console.log('🌿 Silt fence photo previews updated, new count:', updated.length);
              return updated;
            });

            // Start background upload immediately
            uploadPhotoInBackground(file, 'silt_fence');
          }
        });

        setTimeout(() => setSiltFenceCameraCooldown(false), 1000);
      } catch (error) {
        console.error('Error capturing silt fence photo:', error);
        alert('Failed to capture photo');
        setSiltFenceCameraCooldown(false);
      }
    } else {
      // Android: Use custom camera component
      setCurrentCameraType('siltFence');
      setShowCamera(true);
    }
  };

  const removeSiltFencePhoto = (index: number) => {
    setSiltFencePhotos(prev => prev.filter((_, i) => i !== index));
    setSiltFencePhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Unified camera handlers for Android
  const handleCameraCapture = async (imageDataUrl: string) => {
    setShowCamera(false);

    try {
      // Convert base64 to blob
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });

      // Generate preview URL using object URL (memory-efficient vs base64)
      const previewUrl = URL.createObjectURL(file);

      // Route to appropriate handler based on camera type
      if (currentCameraType === 'daily') {
        setPhotos(prev => {
          const updated = [...prev, file];
          console.log('📸 Photos array updated, new count:', updated.length);
          return updated;
        });
        setPhotoPreviews(prev => {
          const updated = [...prev, previewUrl];
          console.log('📸 Photo previews updated, new count:', updated.length);
          return updated;
        });
        // Start background upload immediately
        uploadPhotoInBackground(file, 'daily');
        setCameraCooldown(true);
        setTimeout(() => setCameraCooldown(false), 1000);
      } else if (currentCameraType === 'issue') {
        setCurrentIssue(prev => ({ ...prev, photos: [...prev.photos, file] }));
        setCurrentIssuePhotoPreviews(prev => [...prev, previewUrl]);
        setIssueCameraCooldown(true);
        setTimeout(() => setIssueCameraCooldown(false), 1000);
      } else if (currentCameraType === 'siltFence') {
        setSiltFencePhotos(prev => {
          const updated = [...prev, file];
          console.log('🌿 Silt fence photos array updated, new count:', updated.length);
          return updated;
        });
        setSiltFencePhotoPreviews(prev => {
          const updated = [...prev, previewUrl];
          console.log('🌿 Silt fence photo previews updated, new count:', updated.length);
          return updated;
        });
        // Start background upload immediately for silt fence photos too
        uploadPhotoInBackground(file, 'silt_fence');
        setSiltFenceCameraCooldown(true);
        setTimeout(() => setSiltFenceCameraCooldown(false), 1000);
      }
    } catch (err) {
      console.error('Error processing camera capture:', err);
      setError('Failed to capture photo. Please try again.');

      // Reset appropriate cooldown
      if (currentCameraType === 'daily') {
        setCameraCooldown(false);
      } else if (currentCameraType === 'issue') {
        setIssueCameraCooldown(false);
      } else if (currentCameraType === 'siltFence') {
        setSiltFenceCameraCooldown(false);
      }
    }
  };

  const handleCameraClose = () => {
    setShowCamera(false);

    // Set appropriate cooldown based on camera type
    if (currentCameraType === 'daily') {
      setCameraCooldown(true);
      setTimeout(() => setCameraCooldown(false), 500);
    } else if (currentCameraType === 'issue') {
      setIssueCameraCooldown(true);
      setTimeout(() => setIssueCameraCooldown(false), 500);
    } else if (currentCameraType === 'siltFence') {
      setSiltFenceCameraCooldown(true);
      setTimeout(() => setSiltFenceCameraCooldown(false), 500);
    }
  };

  const removeEquipmentIssue = async (index: number) => {
    const issue = equipmentIssues[index];

    // If issue exists in database, mark it as fixed instead of deleting
    if (issue.issue_id) {
      if (!confirm(`Mark this issue as fixed? This will remove it from the list.`)) {
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/equipment/issues/${issue.issue_id}/resolve`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();
        if (data.success) {
          console.log(`✅ Issue ${issue.issue_id} marked as fixed`);
          setEquipmentIssues(prev => prev.filter((_, i) => i !== index));
        } else {
          alert('Failed to mark issue as fixed: ' + (data.error || 'Unknown error'));
        }
      } catch (err) {
        console.error('Error marking issue as fixed:', err);
        alert('Failed to mark issue as fixed. Please try again.');
      }
    } else {
      // New issue not yet saved - just remove from state
      setEquipmentIssues(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Helper to navigate to next step and save draft
  const goToStep = (nextStep: number) => {
    console.log(`📄 Navigating from step ${step} to step ${nextStep}, saving draft...`);
    saveDraftToLocalStorage(); // Save current progress
    setStep(nextStep);
    // Scroll the main-content container (not window) to top
    const mainContent = document.querySelector('.main-content');
    if (mainContent) mainContent.scrollTo(0, 0);
    else window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!selectedJobId) {
      setError(t.pleaseSelectJob);
      return;
    }
    const descLower = workDescription.trim().toLowerCase();
    const isPhotoOnlyDesc = descLower.includes('photo') && descLower.includes('upload') && descLower.includes('only');
    if (!workDescription.trim() || isPhotoOnlyDesc) {
      setError(t.pleaseDescribeWork);
      return;
    }
    if (!user) {
      setError(t.userNotLoggedIn);
      return;
    }

    try {
      // Mark as submitting to prevent beforeunload warning
      isSubmittingRef.current = true;

      setLoading(true);
      // Show upload modal immediately (skip the "Saving Report..." intermediate message)
      setSuccess(true);
      setUploadStatus('uploading');
      setError(null);

      // Get report date in EST timezone (night work window shifts date forward)
      const today = getReportDateString();

      // Prepare equipment data — rentals only. Company equipment (machines + real
      // hours) is populated server-side from GPS by the 8 PM report-update cron.
      const rentalEquipmentItems = selectedRentalEquipment.map(rental => ({
        equipment_id: null,
        hours_used: 8.0,
        notes: '',
        is_rental: true,
        rental_company: rental.rental_company,
        rental_equipment_type: rental.rental_equipment_type
      }));
      const equipment = [...rentalEquipmentItems];

      // Check which photos are already uploaded vs need uploading
      // IMPORTANT: Exclude silt fence photos from daily photos to prevent duplicates
      let preUploadedDailyPhotos = photoUploads
        .filter(p => p.uploadStatus === 'uploaded' && p.uploadedUrl && photos.includes(p.file) && !siltFencePhotos.includes(p.file))
        .map(p => ({ fullUrl: p.uploadedUrl!, thumbnailUrl: p.thumbnailUrl }));

      let preUploadedSiltFencePhotos = photoUploads
        .filter(p => p.uploadStatus === 'uploaded' && p.uploadedUrl && siltFencePhotos.includes(p.file))
        .map(p => ({ fullUrl: p.uploadedUrl!, thumbnailUrl: p.thumbnailUrl }));

      // RECOVERY: If React state lost URLs but localStorage has them, recover
      // This fixes iOS Safari PWA issue where state can be lost on bad signal
      try {
        const dailyStorageKey = `preupload_${selectedJobId}_daily`;
        const siltStorageKey = `preupload_${selectedJobId}_silt_fence`;

        const storedDaily = JSON.parse(localStorage.getItem(dailyStorageKey) || '[]');
        const storedSilt = JSON.parse(localStorage.getItem(siltStorageKey) || '[]');

        // If state has fewer pre-uploaded photos than localStorage, recover from localStorage
        if (storedDaily.length > 0 && preUploadedDailyPhotos.length < storedDaily.length) {
          console.log(`🔄 RECOVERY: State has ${preUploadedDailyPhotos.length} daily photos, localStorage has ${storedDaily.length}. Using localStorage.`);
          preUploadedDailyPhotos = storedDaily.map((p: any) => ({ fullUrl: p.fullUrl, thumbnailUrl: p.thumbnailUrl }));
        }

        if (storedSilt.length > 0 && preUploadedSiltFencePhotos.length < storedSilt.length) {
          console.log(`🔄 RECOVERY: State has ${preUploadedSiltFencePhotos.length} silt photos, localStorage has ${storedSilt.length}. Using localStorage.`);
          preUploadedSiltFencePhotos = storedSilt.map((p: any) => ({ fullUrl: p.fullUrl, thumbnailUrl: p.thumbnailUrl }));
        }
      } catch (e) {
        console.warn('Failed to recover pre-uploaded URLs from localStorage:', e);
      }

      // Only include files that weren't successfully pre-uploaded
      const pendingDailyPhotos = photos.filter(photo => {
        const upload = photoUploads.find(p => p.file === photo);
        return !upload || upload.uploadStatus !== 'uploaded';
      });

      const pendingSiltFencePhotos = siltFencePhotos.filter(photo => {
        const upload = photoUploads.find(p => p.file === photo);
        return !upload || upload.uploadStatus !== 'uploaded';
      });

      // Prepare files for queue (only photos that weren't pre-uploaded)
      const files = [
        ...pendingDailyPhotos.map(photo => ({
          file: photo,
          filename: photo.name,
          fieldName: 'photos'
        })),
        ...pendingSiltFencePhotos.map(photo => ({
          file: photo,
          filename: photo.name,
          fieldName: 'silt_fence_photos'
        }))
      ];

      console.log(`📤 Preparing to submit report:`);
      console.log(`   - Photos taken: ${photos.length} daily, ${siltFencePhotos.length} silt fence`);
      console.log(`   - Daily photos: ${preUploadedDailyPhotos.length} pre-uploaded, ${pendingDailyPhotos.length} pending`);
      console.log(`   - Silt fence photos: ${preUploadedSiltFencePhotos.length} pre-uploaded, ${pendingSiltFencePhotos.length} pending`);
      console.log(`📤 Total files still need uploading: ${files.length}`);
      files.forEach((f, i) => console.log(`  ${i + 1}. ${f.fieldName}: ${f.filename} (${f.file.size} bytes)`));

      // WARNING: Check for photo loss
      const totalDailyAccounted = preUploadedDailyPhotos.length + pendingDailyPhotos.length;
      const totalSiltAccounted = preUploadedSiltFencePhotos.length + pendingSiltFencePhotos.length;
      if (photos.length > 0 && totalDailyAccounted === 0) {
        console.error(`⚠️ PHOTO LOSS DETECTED: ${photos.length} daily photos taken but 0 accounted for in submission!`);
      }
      if (siltFencePhotos.length > 0 && totalSiltAccounted === 0) {
        console.error(`⚠️ PHOTO LOSS DETECTED: ${siltFencePhotos.length} silt fence photos taken but 0 accounted for in submission!`);
      }

      // Prepare hauled-off loads data (only non-zero counts)
      const haulOffLoads = [
        { load_type: 'dump_truck', material_type: 'fill_dirt', load_count: haulOffFillDirt },
        { load_type: 'dump_truck', material_type: 'baserock', load_count: haulOffBaserock },
        { load_type: 'dump_truck', material_type: 'asphalt', load_count: haulOffAsphalt },
        { load_type: 'dump_truck', material_type: 'asphalt_millings', load_count: haulOffAsphaltMillings },
        { load_type: 'dump_truck', material_type: 'concrete', load_count: haulOffConcreteDump },
        { load_type: 'dump_truck', material_type: 'misc', load_count: haulOffMiscDump },
        { load_type: 'dumpster', material_type: 'concrete', load_count: haulOffConcreteDumpster },
        { load_type: 'dumpster', material_type: 'demolition_debris', load_count: haulOffDemolitionDumpster },
        { load_type: 'dumpster', material_type: 'vegetation', load_count: haulOffVegetationDumpster }
      ].filter(load => load.load_count > 0);

      // Prepare subcontractors data
      const subcontractorsData = selectedSubcontractors.map(typeId => {
        const sub = subcontractorDisplayList.find(s => s.type_id === typeId);
        // Collect work items for this subcontractor
        const typeWorkItems = subcontractorWorkItems[typeId] || {};
        const workItems: any[] = [];
        Object.entries(typeWorkItems).forEach(([templateId, lines]) => {
          (lines as any[]).forEach(line => {
            // Only include lines that have actual data
            if (line.quantity_value || line.length_value || line.width_value || line.depth_value || line.checkbox_value) {
              workItems.push({
                template_id: parseInt(templateId),
                line_number: line.line_number || 1,
                quantity_value: line.quantity_value || null,
                length_value: line.length_value || null,
                width_value: line.width_value || null,
                depth_value: line.depth_value || null,
                depth_unit: line.depth_unit || null,
                checkbox_value: line.checkbox_value || null,
                line_note: line.line_note || null
              });
            }
          });
        });
        return {
          type_id: typeId,
          job_subcontractor_id: sub?.job_subcontractor_id || null,
          notes: subcontractorNotes[typeId] || null,
          work_items: workItems
        };
      });

      // Prepare job needs data
      const jobNeeds = selectedJobNeeds.map(item_id => ({
        item_id: item_id,
        quantity: 1
      }));

      // Save manual counts to shared table before submitting (for PDF generation)
      if (manualTicketEntryEnabled) {
        await saveSharedManualCounts();
      }

      // Add to queue (saves immediately, uploads in background)
      const queueResult = await uploadQueueService.addToQueue(
        'daily_report',
        {
          job_id: selectedJobId,
          report_date: today,
          work_completed_description: workDescription,
          issues: issues,
          // Weather, temperature, crew_count, load_count, and materials_used will be auto-populated by cron at 8 PM
          equipment: JSON.stringify(equipment),
          employees: JSON.stringify([]),
          silt_fence_condition: siltFenceCondition,
          silt_fence_repair_status: siltFenceRepairStatus,
          silt_fence_notes: siltFenceNotes,
          job_needs: JSON.stringify(jobNeeds),
          job_needs_other_text: jobNeedsOtherText,
          // Manual truck ticket entry (if enabled)
          manual_ticket_entry_enabled: manualTicketEntryEnabled,
          manual_loads_fill: manualLoadsFill || 0,
          manual_loads_rock: manualLoadsRock || 0,
          manual_loads_base: manualLoadsBase || 0,
          manual_loads_asphalt: manualLoadsAsphalt || 0,
          manual_loads_other: manualLoadsOther || 0,
          manual_loads_notes: manualLoadsNotes || '',
          // Loads hauled off site
          hauled_off_loads: JSON.stringify(haulOffLoads),
          haul_off_notes: haulOffNotes,
          // Subcontractors on site
          subcontractors: JSON.stringify(subcontractorsData),
          // Pre-uploaded photos (already in Azure, just need to link to report)
          pre_uploaded_photos: JSON.stringify(preUploadedDailyPhotos),
          pre_uploaded_silt_fence_photos: JSON.stringify(preUploadedSiltFencePhotos)
        },
        files,
        user.employee_id,
        selectedJobId
      );

      // Submit equipment issues separately (they will be linked to report via job_id and report_date on server)
      // The server will link them to the report based on matching job_id and report_date
      // ONLY submit NEW issues (without issue_id) - existing issues already in DB shouldn't be re-submitted
      console.log('🔧 DEBUG: Total equipment issues:', equipmentIssues.length);
      equipmentIssues.forEach((issue: any, i: number) => {
        console.log(`  ${i + 1}. Issue ID: ${issue.issue_id || 'NEW'}, Equipment: ${issue.equipment_name}, Desc: ${issue.description?.substring(0, 30)}...`);
      });

      const newIssues = equipmentIssues.filter(issue => !issue.issue_id);
      console.log('🔧 DEBUG: NEW issues to submit (no issue_id):', newIssues.length);
      newIssues.forEach((issue: any, i: number) => {
        console.log(`  ${i + 1}. Equipment: ${issue.equipment_name}, Desc: ${issue.description?.substring(0, 30)}...`);
      });

      for (const issue of newIssues) {
        try {
          const formData = new FormData();
          formData.append('equipment_id', issue.equipment_id.toString());
          formData.append('issue_description', issue.description);
          formData.append('severity', issue.severity);
          formData.append('job_id', selectedJobId.toString());
          formData.append('report_date', today); // Send report date so server can link to the right report

          // Append multiple photos if present
          if (issue.photos && issue.photos.length > 0) {
            issue.photos.forEach((photo) => {
              formData.append('photos', photo); // Multiple files with same field name
            });
          }

          // Try to submit immediately if online, otherwise will be queued
          const response = await fetch(`${API_BASE_URL}/api/equipment/issue`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
            },
            body: formData
          });

          const data = await response.json();
          if (data.success && data.data.issue_id) {
            console.log(`Equipment issue ${data.data.issue_id} submitted successfully`);
          }
        } catch (err) {
          console.error('Error submitting equipment issue:', err);
          // Issues will be queued for later sync if network fails
        }
      }

      if (newIssues.length > 0) {
        console.log(`Submitted ${newIssues.length} new equipment issues`);
      }
      if (equipmentIssues.length > newIssues.length) {
        console.log(`Skipped ${equipmentIssues.length - newIssues.length} existing equipment issues (already in database)`);
      }

      setCurrentUploadId(queueResult);

      // Start tracking upload status
      trackUploadStatus(queueResult);

      // Clear the draft since report was successfully submitted
      clearDraft();

      // Clear pre-upload localStorage cache for this job
      try {
        localStorage.removeItem(`preupload_${selectedJobId}_daily`);
        localStorage.removeItem(`preupload_${selectedJobId}_silt_fence`);
        console.log(`🧹 Cleared pre-upload localStorage cache for job ${selectedJobId}`);
      } catch (e) {
        console.warn('Failed to clear pre-upload localStorage:', e);
      }

      // Reset hasLoadedInitialData so equipment will reload from today's report if user comes back
      setHasLoadedInitialData(false);

      // Don't auto-close - user clicks "Continue" button
      // This lets them see the success message and queue status

    } catch (err) {
      console.error('Error queueing report:', err);
      setError(t.failedToQueueReport);
      setSuccess(false); // Hide upload modal on error
      // Reset submitting flag on error so warning works again
      isSubmittingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  // Loading overlay while initializing (includes report data loading to prevent user typing before data loads)
  const isInitializing = loadingStates.user || loadingStates.jobs || loadingStates.equipment || loadingStates.initializing || loadingStates.report;
  if (isInitializing) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}>
        <Loader2
          size={48}
          style={{
            color: 'white',
            animation: 'spin 1s linear infinite'
          }}
        />
        <div style={{
          color: 'white',
          fontSize: 18,
          marginTop: 20,
          fontWeight: '500'
        }}>
          {t.loading}
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Large uploading overlay (similar to TruckTickets)
  if (isUploading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          maxWidth: '400px',
          width: '100%'
        }}>
          <Cloud size={64} className="animate-pulse" style={{ color: '#3b82f6', margin: '0 auto 16px auto' }} />
          <h2 style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 12, color: '#1e293b' }}>Saving Report...</h2>
          <p style={{ fontSize: 16, color: '#6b7280' }}>Your report is being queued for upload</p>
        </div>
      </div>
    );
  }

  if (success) {
    const isUploadComplete = uploadStatus === 'completed';
    const isUploadFailed = uploadStatus === 'failed';
    const isUploadTimeout = uploadStatus === 'timeout';
    const isStillUploading = uploadStatus === 'uploading';

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          maxWidth: '400px',
          width: '100%'
        }}>
          {/* Icon changes based on status */}
          {isUploadComplete ? (
            <CheckCircle size={64} style={{ color: '#22c55e', margin: '0 auto 16px auto' }} />
          ) : isUploadFailed ? (
            <AlertCircle size={64} style={{ color: '#ef4444', margin: '0 auto 16px auto' }} />
          ) : (
            <Cloud size={64} className="animate-pulse" style={{ color: '#3b82f6', margin: '0 auto 16px auto' }} />
          )}

          {/* Title changes based on status */}
          <h2 style={{
            fontSize: 24,
            fontWeight: 'bold',
            marginBottom: 12,
            color: isUploadComplete ? '#22c55e' : isUploadFailed ? '#ef4444' : '#1e293b'
          }}>
            {isUploadComplete ? t.reportSaved :
             isUploadFailed ? 'Upload Failed' :
             isUploadTimeout ? 'Report Queued' :
             'Uploading Report...'}
          </h2>

          {/* Upload Progress Bar */}
          {isStillUploading && (
            <div style={{ marginBottom: 20 }}>
              <div style={{
                width: '100%',
                height: 8,
                backgroundColor: '#e5e7eb',
                borderRadius: 4,
                overflow: 'hidden',
                marginBottom: 12
              }}>
                <div style={{
                  height: '100%',
                  backgroundColor: '#3b82f6',
                  borderRadius: 4,
                  animation: 'uploadProgress 2s ease-in-out infinite'
                }} />
              </div>
              <p style={{ fontSize: 14, color: '#6b7280' }}>
                {photos.length > 0 || siltFencePhotos.length > 0
                  ? `Uploading ${photos.length + siltFencePhotos.length} photo${photos.length + siltFencePhotos.length > 1 ? 's' : ''}...`
                  : 'Sending report data...'}
              </p>
              <p style={{ fontSize: 13, color: '#9ca3af', marginTop: 8 }}>
                Please wait - this ensures your report is saved
              </p>
            </div>
          )}

          {/* Status message */}
          <p style={{ fontSize: 16, color: '#6b7280', marginBottom: 24 }}>
            {isUploadComplete ? 'Your report has been uploaded successfully!' :
             isUploadFailed ? 'There was an error uploading. It will retry automatically.' :
             isUploadTimeout ? 'Upload will continue in the background.' :
             'Report is queued. Upload will continue even if you leave.'}
          </p>

          {/* Continue button - style changes based on status */}
          <button
            onClick={() => {
              window.location.href = '/';
            }}
            style={{
              width: '100%',
              backgroundColor: isUploadComplete ? '#22c55e' : '#3b82f6',
              color: 'white',
              padding: '16px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
          >
            {isUploadComplete ? 'Done' :
             isStillUploading ? 'Continue' :
             'Continue'}
          </button>

          {/* Info message while uploading - reassure user photos are safe */}
          {isStillUploading && (
            <p style={{ fontSize: 12, color: '#3b82f6', marginTop: 12, fontWeight: 500 }}>
              Photos are saved. You can leave - uploads will finish in background.
            </p>
          )}

          {/* CSS for progress animation */}
          <style>{`
            @keyframes uploadProgress {
              0% { width: 5%; margin-left: 0; }
              50% { width: 40%; margin-left: 30%; }
              100% { width: 5%; margin-left: 95%; }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', paddingBottom: '400px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      {/* Initial loading overlay */}
      {jobs.length === 0 && !hasLoadedInitialData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          gap: '20px'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #e0e0e0',
            borderTop: '4px solid #4facfe',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <div style={{
            fontSize: '18px',
            color: '#64748b',
            fontWeight: '500'
          }}>
            {t.loading}
          </div>
        </div>
      )}

      {/* Location Expired Modal (PWA only) */}
      {isLocationStale && !isNativeApp() && !locationRefreshing && (
        <LocationExpiredModal
          onRefresh={handleRefreshLocation}
          language={language}
        />
      )}

      {/* Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx */}

      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        {/* Pending Upload Recovery Indicator */}
        {loadedFromPendingUpload && !success && (
          <div style={{
            backgroundColor: '#fef3c7',
            border: '2px solid #fcd34d',
            borderRadius: '8px',
            padding: '14px 18px',
            marginBottom: '18px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Cloud size={24} style={{ color: '#f59e0b' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#92400e' }}>
                {language === 'es' ? 'Reporte pendiente de subir' : 'Report pending upload'}
              </div>
              <div style={{ fontSize: '14px', color: '#a16207', marginTop: '4px' }}>
                {language === 'es'
                  ? 'Este reporte fue enviado pero no se ha subido todavía. Se subirá automáticamente cuando mejore la señal.'
                  : 'This report was submitted but hasn\'t uploaded yet. It will upload automatically when signal improves.'}
              </div>
            </div>
          </div>
        )}

        {/* Queue Status Indicator */}
        {queueStats.total > 0 && !success && !loadedFromPendingUpload && (
          <div style={{
            backgroundColor: queueStats.failed > 0 ? '#fef2f2' : '#f0f9ff',
            border: `2px solid ${queueStats.failed > 0 ? '#fee2e2' : '#dbeafe'}`,
            borderRadius: '8px',
            padding: '14px 18px',
            marginBottom: '18px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            {queueStats.uploading > 0 ? (
              <Cloud size={24} className="animate-pulse" style={{ color: '#3b82f6' }} />
            ) : queueStats.failed > 0 ? (
              <CloudOff size={24} style={{ color: '#ef4444' }} />
            ) : (
              <Cloud size={24} style={{ color: '#10b981' }} />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: '600' }}>
                {queueStats.uploading > 0 && `${t.uploading} ${queueStats.uploading} ${queueStats.uploading > 1 ? t.reports : t.report}...`}
                {queueStats.pending > 0 && !queueStats.uploading && `${queueStats.pending} ${queueStats.pending > 1 ? t.reports : t.report} ${t.queued}`}
                {queueStats.failed > 0 && ` ${queueStats.failed} ${t.failed}`}
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                {t.reportsWillUpload}
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: 12,
          padding: 24,
          marginBottom: 24,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
            <FileText style={{ width: 40, height: 40, color: '#3b82f6', marginRight: 16 }} />
            <h1 style={{ fontSize: 30, fontWeight: 'bold', margin: 0 }}>{t.dailyReport}</h1>
          </div>

          {/* Night Shift Banner */}
          {getNightWorkStatus() && (
            <div style={{
              backgroundColor: '#1e1b4b',
              color: '#c4b5fd',
              padding: '10px 16px',
              borderRadius: 8,
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 15,
              fontWeight: 600
            }}>
              <span style={{ fontSize: 20 }}>&#127769;</span>
              {language === 'es' ? 'Reporte de Turno Nocturno' : 'Night Shift Report'}
            </div>
          )}

          {/* Step Indicator */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
            {[1, 3, 4, 5, 8].map(s => (
              <div key={s} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: step >= s ? '#3b82f6' : '#e5e7eb',
                  color: step >= s ? 'white' : '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  fontWeight: 'bold',
                  fontSize: 18
                }}>
                  {s}
                </div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6, fontWeight: '500' }}>
                  {s === 1 && t.jobAndWork}
                  {s === 3 && t.loadsHauledOff}
                  {s === 4 && t.subcontractors}
                  {s === 5 && t.equipment}
                  {s === 8 && t.photos}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            padding: 14,
            backgroundColor: '#dbeafe',
            borderRadius: 8,
            fontSize: 15,
            color: '#1e40af',
            fontWeight: '500'
          }}>
            ℹ️ {t.autoInfoMessage}
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '2px solid #fca5a5',
            borderRadius: 8,
            padding: 14,
            marginBottom: 18,
            display: 'flex',
            alignItems: 'center'
          }}>
            <AlertCircle style={{ width: 24, height: 24, color: '#dc2626', marginRight: 12 }} />
            <span style={{ color: '#991b1b', fontSize: 16, fontWeight: '500' }}>{error}</span>
          </div>
        )}

        {/* Step 1: Job & Work Description */}
        {step === 1 && (
          <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: 22, fontWeight: 'bold', marginBottom: 20 }}>{t.selectJobDescribeWork}</h2>

            <div style={{ marginBottom: 20, position: 'relative' }}>
              <label style={{ display: 'block', fontSize: 18, fontWeight: '600', marginBottom: 10 }}>
                {t.jobSite} *
              </label>
              {selectedJobId ? (
                // Show selected job
                <div
                  style={{
                    width: '100%',
                    padding: 16,
                    fontSize: 20,
                    border: '2px solid #10b981',
                    borderRadius: 8,
                    backgroundColor: '#f0fdf4',
                    color: '#374151',
                    fontWeight: '500',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>{selectedJobName}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedJobId(null);
                      setSelectedJobName('');
                      setJobSearch('');
                      setLocationDetectedJob(false);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#6b7280',
                      cursor: 'pointer',
                      fontSize: 24,
                      padding: 0,
                      lineHeight: 1
                    }}
                  >
                    ×
                  </button>
                </div>
              ) : (
                // Show search box
                <>
                  <input
                    type="text"
                    value={jobSearch}
                    onChange={(e) => setJobSearch(e.target.value)}
                    placeholder="Search by job name, number, or address..."
                    style={{
                      width: '100%',
                      padding: 16,
                      fontSize: 20,
                      border: '2px solid #d1d5db',
                      borderRadius: 8,
                      backgroundColor: 'white',
                      color: '#374151'
                    }}
                  />
                  {filteredJobs.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: 6,
                      backgroundColor: 'white',
                      border: '2px solid #d1d5db',
                      borderRadius: 8,
                      maxHeight: 300,
                      overflowY: 'auto',
                      zIndex: 10,
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                    }}>
                      {filteredJobs.map(job => (
                        <div
                          key={job.job_id}
                          onClick={() => {
                            setSelectedJobId(job.job_id);
                            setSelectedJobName(job.job_name);
                            setJobSearch('');
                          }}
                          style={{
                            padding: 16,
                            borderBottom: '1px solid #e5e7eb',
                            cursor: 'pointer'
                          }}
                        >
                          <div style={{ fontWeight: '600', fontSize: 18 }}>{job.job_name}</div>
                          {job.job_number && (
                            <div style={{ fontSize: 16, color: '#6b7280', marginTop: 4 }}>#{job.job_number}</div>
                          )}
                          {job.address && (
                            <div style={{ fontSize: 14, color: '#9ca3af', marginTop: 2 }}>{job.address}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              <p style={{ fontSize: 15, color: locationDetectedJob ? '#059669' : '#6b7280', marginTop: 6 }}>
                {selectedJobId
                  ? (locationDetectedJob ? `📍 ${t.autoSelectedFromLocation} - Click × to change` : 'Click × to change job')
                  : 'Auto-selects from time entry or location'}
              </p>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <label style={{ fontSize: 18, fontWeight: '600' }}>
                  {getNightWorkStatus() ? (language === 'es' ? 'Trabajo Nocturno Completado' : 'Night Work Completed') : t.workCompletedToday} *
                </label>
                {workDescription.trim() && !aiSuggestions.workDescription && (
                  <button
                    type="button"
                    onClick={() => improveText(workDescription, 'workDescription', setWorkDescription, user?.role !== 'admin')}
                    disabled={aiImproving.workDescription}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '8px 16px',
                      fontSize: 14,
                      fontWeight: '500',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      cursor: aiImproving.workDescription ? 'not-allowed' : 'pointer',
                      opacity: aiImproving.workDescription ? 0.6 : 1
                    }}
                  >
                    <Sparkles size={16} />
                    {aiImproving.workDescription ? 'Improving...' : 'Suggest'}
                  </button>
                )}
              </div>
              <textarea
                value={workDescription}
                onChange={(e) => setWorkDescription(e.target.value)}
                onFocus={(e) => {
                  // Clear placeholder text when user clicks to edit
                  const wd = workDescription.toLowerCase();
                  if (wd.includes('photo') && wd.includes('upload') && wd.includes('only')) {
                    setWorkDescription('');
                  }
                }}
                placeholder={t.describeWhatAccomplished}
                rows={6}
                style={{
                  width: '100%',
                  padding: 16,
                  fontSize: 20,
                  border: '2px solid #d1d5db',
                  borderRadius: 8,
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
              {aiIndicators.workDescription && (
                <div style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  background: aiIndicators.workDescription === 'translated' ? '#fef3c7' : '#dbeafe',
                  color: aiIndicators.workDescription === 'translated' ? '#92400e' : '#1e40af',
                  borderRadius: 6,
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}>
                  <Sparkles size={14} />
                  {aiIndicators.workDescription === 'translated' ? 'Translated from Spanish' : 'Text improved'}
                </div>
              )}
              {aiSuggestions.workDescription && (
                <div style={{
                  marginTop: 12,
                  padding: 16,
                  background: '#f0fdf4',
                  border: '2px solid #86efac',
                  borderRadius: 8
                }}>
                  <div style={{ fontSize: 14, fontWeight: '600', marginBottom: 8, color: '#166534' }}>
                    ✨ Suggested improvement:
                  </div>
                  <div style={{ fontSize: 16, marginBottom: 12, color: '#166534' }}>
                    {aiSuggestions.workDescription}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => acceptSuggestion('workDescription', setWorkDescription)}
                      style={{
                        padding: '8px 16px',
                        fontSize: 14,
                        fontWeight: '500',
                        background: '#16a34a',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer'
                      }}
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      onClick={() => rejectSuggestion('workDescription')}
                      style={{
                        padding: '8px 16px',
                        fontSize: 14,
                        fontWeight: '500',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer'
                      }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <label style={{ fontSize: 18, fontWeight: '600' }}>
                  {getNightWorkStatus() ? (language === 'es' ? 'Problemas Nocturnos (Opcional)' : 'Night Issues / Delays (Optional)') : t.issuesProblemsDelays}
                </label>
                {issues.trim() && !aiSuggestions.issues && (
                  <button
                    type="button"
                    onClick={() => improveText(issues, 'issues', setIssues, user?.role !== 'admin')}
                    disabled={aiImproving.issues}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '8px 16px',
                      fontSize: 14,
                      fontWeight: '500',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      cursor: aiImproving.issues ? 'not-allowed' : 'pointer',
                      opacity: aiImproving.issues ? 0.6 : 1
                    }}
                  >
                    <Sparkles size={16} />
                    {aiImproving.issues ? 'Improving...' : 'Suggest'}
                  </button>
                )}
              </div>
              <textarea
                value={issues}
                onChange={(e) => setIssues(e.target.value)}
                placeholder={t.anyProblemsDelays}
                rows={4}
                style={{
                  width: '100%',
                  padding: 16,
                  fontSize: 20,
                  border: '2px solid #d1d5db',
                  borderRadius: 8,
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
              {aiIndicators.issues && (
                <div style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  background: aiIndicators.issues === 'translated' ? '#fef3c7' : '#dbeafe',
                  color: aiIndicators.issues === 'translated' ? '#92400e' : '#1e40af',
                  borderRadius: 6,
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}>
                  <Sparkles size={14} />
                  {aiIndicators.issues === 'translated' ? 'Translated from Spanish' : 'Text improved'}
                </div>
              )}
              {aiSuggestions.issues && (
                <div style={{
                  marginTop: 12,
                  padding: 16,
                  background: '#f0fdf4',
                  border: '2px solid #86efac',
                  borderRadius: 8
                }}>
                  <div style={{ fontSize: 14, fontWeight: '600', marginBottom: 8, color: '#166534' }}>
                    ✨ Suggested improvement:
                  </div>
                  <div style={{ fontSize: 16, marginBottom: 12, color: '#166534' }}>
                    {aiSuggestions.issues}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => acceptSuggestion('issues', setIssues)}
                      style={{
                        padding: '8px 16px',
                        fontSize: 14,
                        fontWeight: '500',
                        background: '#16a34a',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer'
                      }}
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      onClick={() => rejectSuggestion('issues')}
                      style={{
                        padding: '8px 16px',
                        fontSize: 14,
                        fontWeight: '500',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer'
                      }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Job Needs Section */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 18, fontWeight: '600', marginBottom: 10 }}>
                {t.jobNeedsForTomorrow}
              </label>

              {/* Button to open modal */}
              <button
                type="button"
                onClick={() => setShowJobNeedsModal(true)}
                style={{
                  width: '100%',
                  padding: 16,
                  fontSize: 17,
                  fontWeight: '600',
                  backgroundColor: selectedJobNeeds.length > 0 ? '#dbeafe' : '#f9fafb',
                  color: selectedJobNeeds.length > 0 ? '#1e40af' : '#6b7280',
                  border: selectedJobNeeds.length > 0 ? '2px solid #3b82f6' : '2px solid #d1d5db',
                  borderRadius: 8,
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>
                  {selectedJobNeeds.length > 0
                    ? `${selectedJobNeeds.length} ${selectedJobNeeds.length === 1 ? 'item' : 'items'} selected`
                    : t.selectItemsNeeded}
                </span>
                <Package size={20} />
              </button>

              {/* Display selected items */}
              {selectedJobNeeds.length > 0 && (
                <div style={{
                  marginTop: 12,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8
                }}>
                  {selectedJobNeeds.map(itemId => {
                    const item = jobNeedsItems.find(i => i.item_id === itemId);
                    if (!item) return null;
                    return (
                      <span
                        key={itemId}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '6px 12px',
                          backgroundColor: '#dbeafe',
                          color: '#1e40af',
                          borderRadius: 16,
                          fontSize: 14,
                          fontWeight: '500'
                        }}
                      >
                        {language === 'es' ? item.item_name_es : item.item_name_en}
                        <X
                          size={16}
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedJobNeeds(selectedJobNeeds.filter(id => id !== itemId));
                          }}
                        />
                      </span>
                    );
                  })}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, marginBottom: 8 }}>
                <label style={{ fontSize: 16, fontWeight: '500' }}>
                  {t.otherNeeds}
                </label>
                {jobNeedsOtherText.trim() && !aiSuggestions.jobNeedsOtherText && (
                  <button
                    type="button"
                    onClick={() => improveText(jobNeedsOtherText, 'jobNeedsOtherText', setJobNeedsOtherText, user?.role !== 'admin')}
                    disabled={aiImproving.jobNeedsOtherText}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 12px',
                      fontSize: 13,
                      fontWeight: '500',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      cursor: aiImproving.jobNeedsOtherText ? 'not-allowed' : 'pointer',
                      opacity: aiImproving.jobNeedsOtherText ? 0.6 : 1
                    }}
                  >
                    <Sparkles size={14} />
                    {aiImproving.jobNeedsOtherText ? 'Improving...' : 'Suggest'}
                  </button>
                )}
              </div>
              <textarea
                value={jobNeedsOtherText}
                onChange={(e) => setJobNeedsOtherText(e.target.value)}
                placeholder={t.typeOtherNeeds}
                rows={3}
                style={{
                  width: '100%',
                  padding: 14,
                  fontSize: 18,
                  border: '2px solid #d1d5db',
                  borderRadius: 8,
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
              {aiIndicators.jobNeedsOtherText && (
                <div style={{
                  marginTop: 8,
                  padding: '6px 10px',
                  background: aiIndicators.jobNeedsOtherText === 'translated' ? '#fef3c7' : '#dbeafe',
                  color: aiIndicators.jobNeedsOtherText === 'translated' ? '#92400e' : '#1e40af',
                  borderRadius: 6,
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}>
                  <Sparkles size={12} />
                  {aiIndicators.jobNeedsOtherText === 'translated' ? 'Translated from Spanish' : 'Text improved'}
                </div>
              )}
              {aiSuggestions.jobNeedsOtherText && (
                <div style={{
                  marginTop: 12,
                  padding: 12,
                  background: '#f0fdf4',
                  border: '2px solid #86efac',
                  borderRadius: 8
                }}>
                  <div style={{ fontSize: 13, fontWeight: '600', marginBottom: 6, color: '#166534' }}>
                    ✨ Suggested improvement:
                  </div>
                  <div style={{ fontSize: 15, marginBottom: 10, color: '#166534' }}>
                    {aiSuggestions.jobNeedsOtherText}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => acceptSuggestion('jobNeedsOtherText', setJobNeedsOtherText)}
                      style={{
                        padding: '6px 12px',
                        fontSize: 13,
                        fontWeight: '500',
                        background: '#16a34a',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer'
                      }}
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      onClick={() => rejectSuggestion('jobNeedsOtherText')}
                      style={{
                        padding: '6px 12px',
                        fontSize: 13,
                        fontWeight: '500',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer'
                      }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => {
                const isAdmin = user?.role === 'admin';
                console.log('🔍 DEBUG Step 1 Next button clicked:', { isAdmin });

                // Non-admins always go to Step 2 and see the manual entry form directly
                if (!isAdmin) {
                  setManualTicketEntryEnabled(true); // Show manual entry form directly
                  console.log('🎫 Non-admin - going to Step 2 with manual entry form');
                }
                // Admins will see the "Do you need to add loads?" question first

                goToStep(2);
              }}
              disabled={!selectedJobId || !workDescription.trim() || (workDescription.trim().toLowerCase().includes('photo') && workDescription.trim().toLowerCase().includes('upload') && workDescription.trim().toLowerCase().includes('only'))}
              style={{
                width: '100%',
                padding: 20,
                fontSize: 22,
                fontWeight: 'bold',
                backgroundColor: selectedJobId && workDescription.trim() && !(workDescription.trim().toLowerCase().includes('photo') && workDescription.trim().toLowerCase().includes('upload') && workDescription.trim().toLowerCase().includes('only')) ? '#3b82f6' : '#d1d5db',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: selectedJobId && workDescription.trim() && !(workDescription.trim().toLowerCase().includes('photo') && workDescription.trim().toLowerCase().includes('upload') && workDescription.trim().toLowerCase().includes('only')) ? 'pointer' : 'not-allowed'
              }}
            >
              {user?.role === 'admin' ? 'Next: Load Information' : t.nextTruckTickets}
            </button>
          </div>
        )}

        {/* Step 2: Truck Tickets / Load Entry - Always show when on step 2 */}
        {(() => {
          const isAdmin = user?.role === 'admin';
          const shouldShow = step === 2;
          console.log('🔍 DEBUG Step 2 visibility:', { step, isAdmin, showTicketScanQuestion, existingReportId, adminNeedsLoads, manualTicketEntryEnabled, shouldShow });
          return shouldShow;
        })() && (
          <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: 22, fontWeight: 'bold', marginBottom: 20 }}>
              {user?.role === 'admin' && adminNeedsLoads === null ? 'Load Information' : t.truckTicketEntry}
            </h2>

            {/* Admin Question: Do you need to add loads? */}
            {user?.role === 'admin' && adminNeedsLoads === null ? (
              <div>
                <p style={{ fontSize: 18, marginBottom: 24, color: '#374151' }}>
                  Do you need to add truck loads or material hauled off to this report?
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <button
                    onClick={() => {
                      setAdminNeedsLoads(true);
                      setManualTicketEntryEnabled(true); // Skip the "did you scan tickets" question
                      console.log('🔍 Admin chose to include loads - going directly to manual entry');
                    }}
                    style={{
                      width: '100%',
                      padding: 20,
                      fontSize: 18,
                      fontWeight: 'bold',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 12
                    }}
                  >
                    <CheckCircle size={24} />
                    Yes - Add Load Information
                  </button>

                  <button
                    onClick={() => {
                      setAdminNeedsLoads(false);
                      console.log('🔍 Admin chose to skip loads, moving to step 4');
                      goToStep(4); // Skip to Subcontractors
                    }}
                    style={{
                      width: '100%',
                      padding: 20,
                      fontSize: 18,
                      fontWeight: 'bold',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 12
                    }}
                  >
                    <X size={24} />
                    No - Skip Load Information
                  </button>
                </div>

                {/* Back button */}
                <button
                  onClick={() => goToStep(1)}
                  style={{
                    marginTop: 24,
                    padding: '12px 24px',
                    fontSize: 16,
                    fontWeight: 'bold',
                    backgroundColor: 'white',
                    color: '#6b7280',
                    border: '2px solid #d1d5db',
                    borderRadius: 8,
                    cursor: 'pointer'
                  }}
                >
                  Back
                </button>
              </div>
            ) : !manualTicketEntryEnabled ? (
              // Question: Did you scan all tickets?
              <div>
                <p style={{ fontSize: 18, marginBottom: 24, color: '#374151' }}>{t.didYouScanAll}</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <button
                    onClick={() => {
                      setManualTicketEntryEnabled(false);
                      goToStep(3); // Skip to equipment
                    }}
                    style={{
                      width: '100%',
                      padding: 20,
                      fontSize: 18,
                      fontWeight: 'bold',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer'
                    }}
                  >
                    ✓ {t.yesScannedAll}
                  </button>

                  <button
                    onClick={() => setManualTicketEntryEnabled(true)}
                    style={{
                      width: '100%',
                      padding: 20,
                      fontSize: 18,
                      fontWeight: 'bold',
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer'
                    }}
                  >
                    ✎ {t.noManualEntry}
                  </button>

                  <button
                    onClick={() => {
                      saveDraftToLocalStorage();
                      window.location.href = '/trucks';
                    }}
                    style={{
                      width: '100%',
                      padding: 20,
                      fontSize: 18,
                      fontWeight: 'bold',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4
                    }}
                  >
                    <span>📷 {t.goScanTickets}</span>
                    <span style={{ fontSize: 14, fontWeight: 'normal', opacity: 0.9 }}>({t.progressSaved})</span>
                  </button>
                </div>

                <button
                  onClick={() => goToStep(1)}
                  style={{
                    width: '100%',
                    marginTop: 16,
                    padding: 16,
                    fontSize: 18,
                    fontWeight: 'bold',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer'
                  }}
                >
                  {t.back}
                </button>
              </div>
            ) : (
              // Manual Entry Form
              <>
              {/* Shared Counts Banner */}
              <div style={{
                padding: 12,
                backgroundColor: '#dbeafe',
                borderLeft: '4px solid #3b82f6',
                borderRadius: 8,
                marginBottom: 16
              }}>
                <p style={{ margin: 0, fontSize: 14, color: '#1e40af', fontWeight: 500 }}>
                  ℹ️ These counts are shared with all foremen/admins working this job today
                </p>
              </div>

              <div style={{
                padding: 20,
                backgroundColor: '#ecfdf5',
                borderRadius: 12,
                border: '3px solid #10b981',
                marginBottom: 16
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <span style={{ fontSize: 24 }}>📥</span>
                  <h3 style={{ fontSize: 18, fontWeight: '600', margin: 0, color: '#065f46' }}>
                    {t.enterLoadCounts} (Loads Received)
                  </h3>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Fill */}
                  <div>
                    <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                      {t.fill}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <input
                        type="number"
                        min="0"
                        max="700"
                        value={manualLoadsFill === 0 ? '' : manualLoadsFill}
                        onChange={(e) => setManualLoadsFill(Math.min(700, Math.max(0, parseInt(e.target.value) || 0)))}
                        placeholder="0"
                        style={{
                          flex: 1,
                          padding: 16,
                          fontSize: 18,
                          border: '2px solid #10b981',
                          borderRadius: 8,
                          textAlign: 'center',
                          backgroundColor: 'white'
                        }}
                      />
                      <span style={{ fontSize: 16, color: '#6b7280', minWidth: 60 }}>{t.loads}</span>
                    </div>
                  </div>

                  {/* Rock */}
                  <div>
                    <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                      {t.rock}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <input
                        type="number"
                        min="0"
                        max="700"
                        value={manualLoadsRock === 0 ? '' : manualLoadsRock}
                        onChange={(e) => setManualLoadsRock(Math.min(700, Math.max(0, parseInt(e.target.value) || 0)))}
                        placeholder="0"
                        style={{
                          flex: 1,
                          padding: 16,
                          fontSize: 18,
                          border: '2px solid #10b981',
                          borderRadius: 8,
                          textAlign: 'center',
                          backgroundColor: 'white'
                        }}
                      />
                      <span style={{ fontSize: 16, color: '#6b7280', minWidth: 60 }}>{t.loads}</span>
                    </div>
                  </div>

                  {/* Base */}
                  <div>
                    <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                      {t.base}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <input
                        type="number"
                        min="0"
                        max="700"
                        value={manualLoadsBase === 0 ? '' : manualLoadsBase}
                        onChange={(e) => setManualLoadsBase(Math.min(700, Math.max(0, parseInt(e.target.value) || 0)))}
                        placeholder="0"
                        style={{
                          flex: 1,
                          padding: 16,
                          fontSize: 18,
                          border: '2px solid #10b981',
                          borderRadius: 8,
                          textAlign: 'center',
                          backgroundColor: 'white'
                        }}
                      />
                      <span style={{ fontSize: 16, color: '#6b7280', minWidth: 60 }}>{t.loads}</span>
                    </div>
                  </div>

                  {/* Asphalt */}
                  <div>
                    <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                      {t.asphalt}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <input
                        type="number"
                        min="0"
                        max="700"
                        value={manualLoadsAsphalt === 0 ? '' : manualLoadsAsphalt}
                        onChange={(e) => setManualLoadsAsphalt(Math.min(700, Math.max(0, parseInt(e.target.value) || 0)))}
                        placeholder="0"
                        style={{
                          flex: 1,
                          padding: 16,
                          fontSize: 18,
                          border: '2px solid #10b981',
                          borderRadius: 8,
                          textAlign: 'center',
                          backgroundColor: 'white'
                        }}
                      />
                      <span style={{ fontSize: 16, color: '#6b7280', minWidth: 60 }}>{t.loads}</span>
                    </div>
                  </div>

                  {/* Other */}
                  <div>
                    <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                      {t.other}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <input
                        type="number"
                        min="0"
                        max="700"
                        value={manualLoadsOther === 0 ? '' : manualLoadsOther}
                        onChange={(e) => setManualLoadsOther(Math.min(700, Math.max(0, parseInt(e.target.value) || 0)))}
                        placeholder="0"
                        style={{
                          flex: 1,
                          padding: 16,
                          fontSize: 18,
                          border: '2px solid #10b981',
                          borderRadius: 8,
                          textAlign: 'center',
                          backgroundColor: 'white'
                        }}
                      />
                      <span style={{ fontSize: 16, color: '#6b7280', minWidth: 60 }}>{t.loads}</span>
                    </div>
                  </div>

                  {/* Other Materials Notes */}
                  <div>
                    <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                      {t.otherMaterialsNotes}
                    </label>
                    <textarea
                      value={manualLoadsNotes}
                      onChange={(e) => setManualLoadsNotes(e.target.value)}
                      placeholder={t.describeOtherMaterials}
                      style={{
                        width: '100%',
                        padding: 16,
                        fontSize: 16,
                        border: '2px solid #10b981',
                        borderRadius: 8,
                        minHeight: 80,
                        resize: 'vertical',
                        backgroundColor: 'white'
                      }}
                    />
                  </div>
                </div>

                {/* Total Count Display */}
                <div style={{
                  marginTop: 20,
                  padding: 16,
                  backgroundColor: '#d1fae5',
                  borderRadius: 8,
                  border: '2px solid #10b981'
                }}>
                  <p style={{ fontSize: 18, fontWeight: 'bold', color: '#065f46', textAlign: 'center' }}>
                    Total Received: {manualLoadsFill + manualLoadsRock + manualLoadsBase + manualLoadsAsphalt + manualLoadsOther} {t.loads}
                  </p>
                </div>
              </div>

                {/* Navigation Buttons */}
                <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                  <button
                    onClick={() => {
                      // If setting is ON, go back to question; if OFF, go to Step 1
                      if (showTicketScanQuestion || existingReportId) {
                        setManualTicketEntryEnabled(false);
                      } else {
                        goToStep(1);
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: 16,
                      fontSize: 18,
                      fontWeight: 'bold',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer'
                    }}
                  >
                    {t.back}
                  </button>

                  <button
                    onClick={() => goToStep(3)}
                    style={{
                      flex: 2,
                      padding: 20,
                      fontSize: 20,
                      fontWeight: 'bold',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer'
                    }}
                  >
                    {t.nextLoadsHauledOff} →
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 3: Loads Hauled Off Site */}
        {step === 3 && (user?.role !== 'admin' || adminNeedsLoads !== false) && (
          <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <span style={{ fontSize: 24 }}>📤</span>
              <h2 style={{ fontSize: 22, fontWeight: 'bold', margin: 0, color: '#9a3412' }}>{t.loadsHauledOffTitle}</h2>
            </div>

            {/* Dump Truck Loads Section */}
            <div style={{
              padding: 20,
              backgroundColor: '#fff7ed',
              borderRadius: 12,
              border: '3px solid #f97316',
              marginBottom: 24
            }}>
              <h3 style={{ fontSize: 18, fontWeight: '600', marginBottom: 16, color: '#9a3412' }}>
                🚛 {t.dumpTruckLoads}
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Fill Dirt */}
                <div>
                  <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                    {t.fillDirt}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input
                      type="number"
                      min="0"
                      max="500"
                      value={haulOffFillDirt === 0 ? '' : haulOffFillDirt}
                      onChange={(e) => setHaulOffFillDirt(Math.min(500, Math.max(0, parseInt(e.target.value) || 0)))}
                      placeholder="0"
                      style={{
                        flex: 1,
                        padding: 16,
                        fontSize: 18,
                        border: '2px solid #f97316',
                        borderRadius: 8,
                        textAlign: 'center',
                        backgroundColor: 'white'
                      }}
                    />
                    <span style={{ fontSize: 16, color: '#6b7280', minWidth: 60 }}>{t.trucks}</span>
                  </div>
                </div>

                {/* Baserock */}
                <div>
                  <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                    {t.baserock}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input
                      type="number"
                      min="0"
                      max="500"
                      value={haulOffBaserock === 0 ? '' : haulOffBaserock}
                      onChange={(e) => setHaulOffBaserock(Math.min(500, Math.max(0, parseInt(e.target.value) || 0)))}
                      placeholder="0"
                      style={{
                        flex: 1,
                        padding: 16,
                        fontSize: 18,
                        border: '2px solid #f97316',
                        borderRadius: 8,
                        textAlign: 'center',
                        backgroundColor: 'white'
                      }}
                    />
                    <span style={{ fontSize: 16, color: '#6b7280', minWidth: 60 }}>{t.trucks}</span>
                  </div>
                </div>

                {/* Asphalt */}
                <div>
                  <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                    {t.asphalt}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input
                      type="number"
                      min="0"
                      max="500"
                      value={haulOffAsphalt === 0 ? '' : haulOffAsphalt}
                      onChange={(e) => setHaulOffAsphalt(Math.min(500, Math.max(0, parseInt(e.target.value) || 0)))}
                      placeholder="0"
                      style={{
                        flex: 1,
                        padding: 16,
                        fontSize: 18,
                        border: '2px solid #f97316',
                        borderRadius: 8,
                        textAlign: 'center',
                        backgroundColor: 'white'
                      }}
                    />
                    <span style={{ fontSize: 16, color: '#6b7280', minWidth: 60 }}>{t.trucks}</span>
                  </div>
                </div>

                {/* Asphalt Millings */}
                <div>
                  <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                    {t.asphaltMillings}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input
                      type="number"
                      min="0"
                      max="500"
                      value={haulOffAsphaltMillings === 0 ? '' : haulOffAsphaltMillings}
                      onChange={(e) => setHaulOffAsphaltMillings(Math.min(500, Math.max(0, parseInt(e.target.value) || 0)))}
                      placeholder="0"
                      style={{
                        flex: 1,
                        padding: 16,
                        fontSize: 18,
                        border: '2px solid #f97316',
                        borderRadius: 8,
                        textAlign: 'center',
                        backgroundColor: 'white'
                      }}
                    />
                    <span style={{ fontSize: 16, color: '#6b7280', minWidth: 60 }}>{t.trucks}</span>
                  </div>
                </div>

                {/* Concrete (Dump Truck) */}
                <div>
                  <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                    {t.concreteDump}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input
                      type="number"
                      min="0"
                      max="500"
                      value={haulOffConcreteDump === 0 ? '' : haulOffConcreteDump}
                      onChange={(e) => setHaulOffConcreteDump(Math.min(500, Math.max(0, parseInt(e.target.value) || 0)))}
                      placeholder="0"
                      style={{
                        flex: 1,
                        padding: 16,
                        fontSize: 18,
                        border: '2px solid #f97316',
                        borderRadius: 8,
                        textAlign: 'center',
                        backgroundColor: 'white'
                      }}
                    />
                    <span style={{ fontSize: 16, color: '#6b7280', minWidth: 60 }}>{t.trucks}</span>
                  </div>
                </div>

                {/* Misc (Dump Truck) */}
                <div>
                  <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                    {t.miscDump}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input
                      type="number"
                      min="0"
                      max="500"
                      value={haulOffMiscDump === 0 ? '' : haulOffMiscDump}
                      onChange={(e) => setHaulOffMiscDump(Math.min(500, Math.max(0, parseInt(e.target.value) || 0)))}
                      placeholder="0"
                      style={{
                        flex: 1,
                        padding: 16,
                        fontSize: 18,
                        border: '2px solid #f97316',
                        borderRadius: 8,
                        textAlign: 'center',
                        backgroundColor: 'white'
                      }}
                    />
                    <span style={{ fontSize: 16, color: '#6b7280', minWidth: 60 }}>{t.trucks}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Dumpster Loads Section */}
            <div style={{
              padding: 20,
              backgroundColor: '#fff7ed',
              borderRadius: 12,
              border: '3px solid #f97316',
              marginBottom: 24
            }}>
              <h3 style={{ fontSize: 18, fontWeight: '600', marginBottom: 16, color: '#9a3412' }}>
                🗑️ {t.dumpsterLoads}
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Concrete (Dumpster) */}
                <div>
                  <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                    {t.concreteDumpster}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={haulOffConcreteDumpster === 0 ? '' : haulOffConcreteDumpster}
                      onChange={(e) => setHaulOffConcreteDumpster(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                      placeholder="0"
                      style={{
                        flex: 1,
                        padding: 16,
                        fontSize: 18,
                        border: '2px solid #f97316',
                        borderRadius: 8,
                        textAlign: 'center',
                        backgroundColor: 'white'
                      }}
                    />
                    <span style={{ fontSize: 16, color: '#6b7280', minWidth: 80 }}>{t.dumpsters}</span>
                  </div>
                </div>

                {/* Demolition Debris */}
                <div>
                  <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                    {t.demolitionDumpster}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={haulOffDemolitionDumpster === 0 ? '' : haulOffDemolitionDumpster}
                      onChange={(e) => setHaulOffDemolitionDumpster(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                      placeholder="0"
                      style={{
                        flex: 1,
                        padding: 16,
                        fontSize: 18,
                        border: '2px solid #f97316',
                        borderRadius: 8,
                        textAlign: 'center',
                        backgroundColor: 'white'
                      }}
                    />
                    <span style={{ fontSize: 16, color: '#6b7280', minWidth: 80 }}>{t.dumpsters}</span>
                  </div>
                </div>

                {/* Vegetation */}
                <div>
                  <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                    {t.vegetationDumpster}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={haulOffVegetationDumpster === 0 ? '' : haulOffVegetationDumpster}
                      onChange={(e) => setHaulOffVegetationDumpster(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                      placeholder="0"
                      style={{
                        flex: 1,
                        padding: 16,
                        fontSize: 18,
                        border: '2px solid #f97316',
                        borderRadius: 8,
                        textAlign: 'center',
                        backgroundColor: 'white'
                      }}
                    />
                    <span style={{ fontSize: 16, color: '#6b7280', minWidth: 80 }}>{t.dumpsters}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Total Counter */}
            <div style={{
              marginTop: 24,
              padding: 16,
              backgroundColor: '#fed7aa',
              borderRadius: 8,
              border: '2px solid #f97316',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: 18, fontWeight: '600', color: '#9a3412' }}>{t.totalRemoved}:</span>
              <span style={{ fontSize: 24, fontWeight: 'bold', color: '#9a3412' }}>
                {haulOffFillDirt + haulOffBaserock + haulOffAsphalt + haulOffAsphaltMillings +
                 haulOffConcreteDump + haulOffMiscDump + haulOffConcreteDumpster +
                 haulOffDemolitionDumpster + haulOffVegetationDumpster}
              </span>
            </div>

            {/* Notes */}
            <div style={{ marginTop: 24 }}>
              <label style={{ display: 'block', fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                {t.haulOffNotes}
              </label>
              <textarea
                value={haulOffNotes}
                onChange={(e) => setHaulOffNotes(e.target.value)}
                placeholder={t.describeHaulOffDetails}
                rows={3}
                style={{
                  width: '100%',
                  padding: 16,
                  fontSize: 16,
                  border: '2px solid #f97316',
                  borderRadius: 8,
                  resize: 'vertical',
                  backgroundColor: 'white'
                }}
              />
            </div>

            {/* Navigation */}
            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button
                onClick={() => goToStep(2)}
                style={{
                  flex: 1,
                  padding: 16,
                  fontSize: 18,
                  fontWeight: 'bold',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                {t.back}
              </button>

              <button
                onClick={() => goToStep(4)}
                style={{
                  flex: 2,
                  padding: 20,
                  fontSize: 20,
                  fontWeight: 'bold',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                {t.nextSubcontractors} →
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Subcontractors On Site */}
        {step === 4 && (
          <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: 22, fontWeight: 'bold', marginBottom: 20 }}>{t.subcontractorsOnSite}</h2>

            {hadSubcontractors === null ? (
              // Question: Did any subcontractors work today?
              <div>
                <p style={{ fontSize: 18, marginBottom: 24, color: '#374151' }}>{t.anySubcontractorsToday}</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <button
                    onClick={() => {
                      setHadSubcontractors(false);
                      goToStep(5); // Skip to equipment
                    }}
                    style={{
                      width: '100%',
                      padding: 20,
                      fontSize: 18,
                      fontWeight: 'bold',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer'
                    }}
                  >
                    {t.no}
                  </button>

                  <button
                    onClick={() => setHadSubcontractors(true)}
                    style={{
                      width: '100%',
                      padding: 20,
                      fontSize: 18,
                      fontWeight: 'bold',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer'
                    }}
                  >
                    {t.yes}
                  </button>
                </div>

                <button
                  onClick={() => goToStep(3)}
                  style={{
                    width: '100%',
                    marginTop: 16,
                    padding: 16,
                    fontSize: 18,
                    fontWeight: 'bold',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer'
                  }}
                >
                  {t.back}
                </button>
              </div>
            ) : (
              // Subcontractor Selection
              <div>
                <h3 style={{ fontSize: 18, fontWeight: '600', marginBottom: 20, color: '#374151' }}>{t.selectSubcontractors}</h3>

                {subcontractorDisplayList.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {subcontractorDisplayList.map(sub => (
                      <div key={sub.type_id} style={{
                        padding: 16,
                        border: '2px solid #d1d5db',
                        borderRadius: 8,
                        backgroundColor: selectedSubcontractors.includes(sub.type_id) ? '#eff6ff' : 'white'
                      }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          cursor: 'pointer',
                          fontSize: 16
                        }}>
                          <input
                            type="checkbox"
                            checked={selectedSubcontractors.includes(sub.type_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedSubcontractors(prev => [...prev, sub.type_id]);
                              } else {
                                setSelectedSubcontractors(prev => prev.filter(id => id !== sub.type_id));
                                // Remove notes when unchecked
                                const newNotes = { ...subcontractorNotes };
                                delete newNotes[sub.type_id];
                                setSubcontractorNotes(newNotes);
                                // Remove work items when unchecked
                                const newWorkItems = { ...subcontractorWorkItems };
                                delete newWorkItems[sub.type_id];
                                setSubcontractorWorkItems(newWorkItems);
                              }
                            }}
                            style={{
                              width: 20,
                              height: 20,
                              marginRight: 12,
                              marginTop: 2,
                              cursor: 'pointer'
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', color: '#374151' }}>
                              {sub.type_name}
                            </div>
                            {sub.company && (
                              <div style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>
                                {sub.company}
                                {sub.phone && ` • ${sub.phone}`}
                              </div>
                            )}
                          </div>
                        </label>

                        {/* Notes textarea for selected subcontractors */}
                        {selectedSubcontractors.includes(sub.type_id) && (
                          <div style={{ marginTop: 12 }}>
                            <textarea
                              value={subcontractorNotes[sub.type_id] || ''}
                              onChange={(e) => setSubcontractorNotes(prev => ({
                                ...prev,
                                [sub.type_id]: e.target.value
                              }))}
                              placeholder={t.notesForSubcontractor}
                              rows={2}
                              style={{
                                width: '100%',
                                padding: 12,
                                fontSize: 14,
                                border: '1px solid #d1d5db',
                                borderRadius: 6,
                                resize: 'vertical'
                              }}
                            />

                            {/* Work Items for this subcontractor type */}
                            {workItemTemplates[sub.type_id] && workItemTemplates[sub.type_id].length > 0 && (
                              <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                                <div style={{ fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 12 }}>
                                  {language === 'es' ? 'Elementos de Trabajo' : 'Work Items'}
                                </div>
                                {workItemTemplates[sub.type_id].map((template: any) => {
                                  const templateLines = subcontractorWorkItems[sub.type_id]?.[template.template_id] || [{ line_number: 1 }];
                                  return (
                                    <div key={template.template_id} style={{ marginBottom: 12, padding: 10, backgroundColor: 'white', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                                      <div style={{ fontSize: 13, fontWeight: '500', color: '#374151', marginBottom: 8 }}>
                                        {language === 'es' && template.item_name_es ? template.item_name_es : template.item_name}
                                        {template.is_required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
                                      </div>
                                      {templateLines.map((line: any, lineIndex: number) => (
                                        <div key={lineIndex} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                                          {template.input_type === 'quantity_unit' && (
                                            <>
                                              <input
                                                type="number"
                                                value={line.quantity_value || ''}
                                                onChange={(e) => {
                                                  const val = e.target.value ? parseFloat(e.target.value) : null;
                                                  setSubcontractorWorkItems(prev => {
                                                    const updated = { ...prev };
                                                    if (!updated[sub.type_id]) updated[sub.type_id] = {};
                                                    if (!updated[sub.type_id][template.template_id]) updated[sub.type_id][template.template_id] = [{ line_number: 1 }];
                                                    updated[sub.type_id][template.template_id][lineIndex] = { ...updated[sub.type_id][template.template_id][lineIndex], quantity_value: val };
                                                    return updated;
                                                  });
                                                }}
                                                placeholder="0"
                                                style={{ flex: 1, padding: 8, fontSize: 14, border: '1px solid #d1d5db', borderRadius: 4, maxWidth: 100 }}
                                              />
                                              <span style={{ fontSize: 13, color: '#6b7280' }}>{template.unit_label || ''}</span>
                                            </>
                                          )}
                                          {template.input_type === 'dimensions' && (
                                            <>
                                              <input
                                                type="number"
                                                value={line.length_value || ''}
                                                onChange={(e) => {
                                                  const val = e.target.value ? parseFloat(e.target.value) : null;
                                                  setSubcontractorWorkItems(prev => {
                                                    const updated = { ...prev };
                                                    if (!updated[sub.type_id]) updated[sub.type_id] = {};
                                                    if (!updated[sub.type_id][template.template_id]) updated[sub.type_id][template.template_id] = [{ line_number: 1 }];
                                                    updated[sub.type_id][template.template_id][lineIndex] = { ...updated[sub.type_id][template.template_id][lineIndex], length_value: val };
                                                    return updated;
                                                  });
                                                }}
                                                placeholder="L"
                                                style={{ width: 50, padding: 8, fontSize: 14, border: '1px solid #d1d5db', borderRadius: 4 }}
                                              />
                                              <span style={{ color: '#9ca3af' }}>×</span>
                                              <input
                                                type="number"
                                                value={line.width_value || ''}
                                                onChange={(e) => {
                                                  const val = e.target.value ? parseFloat(e.target.value) : null;
                                                  setSubcontractorWorkItems(prev => {
                                                    const updated = { ...prev };
                                                    if (!updated[sub.type_id]) updated[sub.type_id] = {};
                                                    if (!updated[sub.type_id][template.template_id]) updated[sub.type_id][template.template_id] = [{ line_number: 1 }];
                                                    updated[sub.type_id][template.template_id][lineIndex] = { ...updated[sub.type_id][template.template_id][lineIndex], width_value: val };
                                                    return updated;
                                                  });
                                                }}
                                                placeholder="W"
                                                style={{ width: 50, padding: 8, fontSize: 14, border: '1px solid #d1d5db', borderRadius: 4 }}
                                              />
                                              <span style={{ color: '#9ca3af' }}>×</span>
                                              <input
                                                type="number"
                                                value={line.depth_value || ''}
                                                onChange={(e) => {
                                                  const val = e.target.value ? parseFloat(e.target.value) : null;
                                                  setSubcontractorWorkItems(prev => {
                                                    const updated = { ...prev };
                                                    if (!updated[sub.type_id]) updated[sub.type_id] = {};
                                                    if (!updated[sub.type_id][template.template_id]) updated[sub.type_id][template.template_id] = [{ line_number: 1 }];
                                                    updated[sub.type_id][template.template_id][lineIndex] = { ...updated[sub.type_id][template.template_id][lineIndex], depth_value: val };
                                                    return updated;
                                                  });
                                                }}
                                                placeholder="D"
                                                style={{ width: 50, padding: 8, fontSize: 14, border: '1px solid #d1d5db', borderRadius: 4 }}
                                              />
                                              <CustomSelect
                                                value={line.depth_unit || 'in'}
                                                onChange={(value) => {
                                                  setSubcontractorWorkItems(prev => {
                                                    const updated = { ...prev };
                                                    if (!updated[sub.type_id]) updated[sub.type_id] = {};
                                                    if (!updated[sub.type_id][template.template_id]) updated[sub.type_id][template.template_id] = [{ line_number: 1 }];
                                                    updated[sub.type_id][template.template_id][lineIndex] = { ...updated[sub.type_id][template.template_id][lineIndex], depth_unit: value };
                                                    return updated;
                                                  });
                                                }}
                                                options={[
                                                  { value: 'in', label: '"' },
                                                  { value: 'ft', label: "'" }
                                                ]}
                                                placeholder={language === 'es' ? 'Unidad' : 'Unit'}
                                                style={{ width: 70, padding: '8px', fontSize: '12px' }}
                                              />
                                            </>
                                          )}
                                          {template.input_type === 'checkbox' && (
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                              <input
                                                type="checkbox"
                                                checked={line.checkbox_value || false}
                                                onChange={(e) => {
                                                  setSubcontractorWorkItems(prev => {
                                                    const updated = { ...prev };
                                                    if (!updated[sub.type_id]) updated[sub.type_id] = {};
                                                    if (!updated[sub.type_id][template.template_id]) updated[sub.type_id][template.template_id] = [{ line_number: 1 }];
                                                    updated[sub.type_id][template.template_id][lineIndex] = { ...updated[sub.type_id][template.template_id][lineIndex], checkbox_value: e.target.checked };
                                                    return updated;
                                                  });
                                                }}
                                                style={{ width: 18, height: 18 }}
                                              />
                                              <span style={{ fontSize: 13, color: '#374151' }}>{language === 'es' ? 'Completado' : 'Completed'}</span>
                                            </label>
                                          )}
                                          {lineIndex > 0 && (
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setSubcontractorWorkItems(prev => {
                                                  const updated = { ...prev };
                                                  updated[sub.type_id][template.template_id] = updated[sub.type_id][template.template_id].filter((_: any, i: number) => i !== lineIndex);
                                                  return updated;
                                                });
                                              }}
                                              style={{ padding: '4px 8px', fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}
                                            >
                                              ✕
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                      {template.input_type !== 'checkbox' && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setSubcontractorWorkItems(prev => {
                                              const updated = { ...prev };
                                              if (!updated[sub.type_id]) updated[sub.type_id] = {};
                                              if (!updated[sub.type_id][template.template_id]) updated[sub.type_id][template.template_id] = [{ line_number: 1 }];
                                              const nextLineNum = updated[sub.type_id][template.template_id].length + 1;
                                              updated[sub.type_id][template.template_id] = [...updated[sub.type_id][template.template_id], { line_number: nextLineNum }];
                                              return updated;
                                            });
                                          }}
                                          style={{ fontSize: 12, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}
                                        >
                                          + {language === 'es' ? 'Agregar Línea' : 'Add Line'}
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize: 16, color: '#6b7280', fontStyle: 'italic' }}>
                    {t.noSubcontractorsAssigned}
                  </p>
                )}

                {/* Navigation */}
                <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                  <button
                    onClick={() => setHadSubcontractors(null)}
                    style={{
                      flex: 1,
                      padding: 16,
                      fontSize: 18,
                      fontWeight: 'bold',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer'
                    }}
                  >
                    {t.back}
                  </button>

                  <button
                    onClick={() => goToStep(5)}
                    style={{
                      flex: 2,
                      padding: 20,
                      fontSize: 20,
                      fontWeight: 'bold',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer'
                    }}
                  >
                    {t.nextEquipmentFromSubs} →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 5: Equipment (renumbered from old Step 3) */}
        {step === 5 && (
          <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: 22, fontWeight: 'bold', marginBottom: 20, display: 'flex', alignItems: 'center' }}>
              <Wrench style={{ width: 24, height: 24, marginRight: 10 }} />
              {language === 'es' ? 'Equipo de Alquiler' : 'Rental Equipment'}
            </h2>

            {/* Company equipment is auto-populated from GPS by the nightly report update */}
            <div style={{
              marginBottom: 24,
              padding: 16,
              backgroundColor: '#eff6ff',
              border: '2px solid #3b82f6',
              borderRadius: 8,
              fontSize: 16,
              color: '#1e40af'
            }}>
              {language === 'es'
                ? 'El equipo de la empresa se rastrea por GPS — las máquinas y sus horas se agregan a este reporte automáticamente. Solo agregue el equipo de alquiler abajo.'
                : 'Company equipment is GPS-tracked — machines and their hours are added to this report automatically. Only add rental equipment below.'}
            </div>

            {/* Rental Equipment Section */}
            <div style={{ marginBottom: 24 }}>
              <p style={{ fontSize: 18, fontWeight: '600', marginBottom: 12, color: '#374151' }}>
                {language === 'es' ? 'Equipo de Alquiler' : 'Rental Equipment'} ({selectedRentalEquipment.length})
              </p>

              {/* Rental Equipment Chips */}
              {selectedRentalEquipment.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
                  {selectedRentalEquipment.map((rental, idx) => (
                    <div
                      key={`rental-${rental.rental_company}-${rental.rental_equipment_type}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 16px',
                        backgroundColor: '#fef3c7',
                        border: '2px solid #f59e0b',
                        borderRadius: 24,
                        fontSize: 16
                      }}
                    >
                      <span style={{
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 'bold'
                      }}>
                        {language === 'es' ? 'Alquiler' : 'Rental'}
                      </span>
                      <span style={{ fontWeight: '600' }}>
                        {rental.rental_company} - {rental.rental_equipment_type}
                      </span>
                      <button
                        onClick={() => setSelectedRentalEquipment(prev => prev.filter((_, i) => i !== idx))}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#f59e0b',
                          cursor: 'pointer',
                          fontSize: 22,
                          lineHeight: 1,
                          padding: 0,
                          fontWeight: 'bold'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Rental Equipment Form */}
              {!showRentalForm ? (
                <button
                  onClick={() => setShowRentalForm(true)}
                  style={{
                    padding: '12px 20px',
                    fontSize: 16,
                    fontWeight: '600',
                    backgroundColor: '#fef3c7',
                    color: '#92400e',
                    border: '2px dashed #f59e0b',
                    borderRadius: 8,
                    cursor: 'pointer',
                    width: '100%'
                  }}
                >
                  + {language === 'es' ? 'Agregar Equipo de Alquiler' : 'Add Rental Equipment'}
                </button>
              ) : (
                <div style={{
                  padding: 16,
                  backgroundColor: '#fffbeb',
                  border: '2px solid #f59e0b',
                  borderRadius: 8
                }}>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 16, fontWeight: '600', color: '#374151', display: 'block', marginBottom: 6 }}>
                      {language === 'es' ? 'Empresa de Alquiler' : 'Rental Company'}
                    </label>
                    <CustomSelect
                      value={rentalCompany}
                      onChange={(value) => setRentalCompany(value)}
                      placeholder={language === 'es' ? 'Seleccionar empresa...' : 'Select rental company...'}
                      options={rentalCompanies.map(company => ({
                        value: company,
                        label: company
                      }))}
                    />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 16, fontWeight: '600', color: '#374151', display: 'block', marginBottom: 6 }}>
                      {language === 'es' ? 'Tipo de Equipo' : 'Equipment Type'}
                    </label>
                    <CustomSelect
                      value={rentalEquipmentType}
                      onChange={(value) => setRentalEquipmentType(value)}
                      placeholder={language === 'es' ? 'Seleccionar tipo...' : 'Select equipment type...'}
                      options={rentalEquipmentTypes.map(type => ({
                        value: type,
                        label: type
                      }))}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={() => {
                        if (rentalCompany && rentalEquipmentType) {
                          const exists = selectedRentalEquipment.some(
                            r => r.rental_company === rentalCompany && r.rental_equipment_type === rentalEquipmentType
                          );
                          if (!exists) {
                            setSelectedRentalEquipment(prev => [...prev, {
                              rental_company: rentalCompany,
                              rental_equipment_type: rentalEquipmentType
                            }]);
                          }
                          setRentalCompany('');
                          setRentalEquipmentType('');
                          setShowRentalForm(false);
                        }
                      }}
                      disabled={!rentalCompany || !rentalEquipmentType}
                      style={{
                        flex: 1,
                        padding: 14,
                        fontSize: 16,
                        fontWeight: 'bold',
                        backgroundColor: rentalCompany && rentalEquipmentType ? '#f59e0b' : '#d1d5db',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        cursor: rentalCompany && rentalEquipmentType ? 'pointer' : 'not-allowed'
                      }}
                    >
                      {language === 'es' ? 'Agregar' : 'Add'}
                    </button>
                    <button
                      onClick={() => {
                        setShowRentalForm(false);
                        setRentalCompany('');
                        setRentalEquipmentType('');
                      }}
                      style={{
                        padding: '14px 20px',
                        fontSize: 16,
                        fontWeight: 'bold',
                        backgroundColor: '#e5e7eb',
                        color: '#374151',
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer'
                      }}
                    >
                      {language === 'es' ? 'Cancelar' : 'Cancel'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => goToStep(4)}
                style={{
                  flex: 1,
                  padding: 20,
                  fontSize: 20,
                  fontWeight: 'bold',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                {t.back}
              </button>
              <button
                onClick={() => goToStep(6)}
                style={{
                  flex: 1,
                  padding: 20,
                  fontSize: 20,
                  fontWeight: 'bold',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                {t.nextEquipmentIssues}
              </button>
            </div>
          </div>
        )}

        {/* Step 6: Equipment Issues (renumbered from old Step 4) */}
        {step === 6 && (
          <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: 22, fontWeight: 'bold', marginBottom: 12, display: 'flex', alignItems: 'center' }}>
              <AlertTriangle style={{ width: 24, height: 24, marginRight: 10, color: '#f59e0b' }} />
              {t.anyEquipmentIssues}
            </h2>
            <p style={{ fontSize: 16, color: '#6b7280', marginBottom: 24 }}>
              {t.reportEquipmentProblems}
            </p>

            {!showIssueForm && equipmentIssues.length === 0 && (
              <div style={{ marginBottom: 24 }}>
                <button
                  onClick={() => {
                    setShowIssueForm(true);
                    // Pre-select first equipment from selected list
                    if (selectedEquipment.length > 0) {
                      setCurrentIssue(prev => ({ ...prev, equipment_id: selectedEquipment[0] }));
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: 20,
                    fontSize: 20,
                    fontWeight: 'bold',
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer'
                  }}
                >
                  {t.yesReportIssue}
                </button>
              </div>
            )}

            {/* Issue Form */}
            {showIssueForm && (
              <div style={{ marginBottom: 24, padding: 20, backgroundColor: '#fef3c7', borderRadius: 8, border: '2px solid #f59e0b' }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 18, fontWeight: '600', marginBottom: 10 }}>
                    {t.selectEquipmentForIssue} *
                  </label>
                  <CustomSelect
                    value={currentIssue.equipment_id || ''}
                    onChange={(value) => setCurrentIssue(prev => ({ ...prev, equipment_id: parseInt(value) }))}
                    placeholder={t.selectEquipmentWithIssue}
                    options={[
                      // Machines GPS saw at this job today first, then the rest of the fleet
                      ...allEquipment.filter(equip => selectedEquipment.includes(equip.equipment_id)),
                      ...allEquipment.filter(equip => !selectedEquipment.includes(equip.equipment_id))
                    ].map(equip => ({
                      value: equip.equipment_id,
                      label: `${equip.equipment_name} ${equip.bradanna_equipment_number ? `#${equip.bradanna_equipment_number}` : ''}`
                    }))}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <label style={{ fontSize: 18, fontWeight: '600' }}>
                      {t.issueDescription} *
                    </label>
                    {currentIssue.description.trim() && !aiSuggestions.equipmentIssueDescription && (
                      <button
                        type="button"
                        onClick={() => improveText(currentIssue.description, 'equipmentIssueDescription', (val) => setCurrentIssue(prev => ({ ...prev, description: val })), user?.role !== 'admin')}
                        disabled={aiImproving.equipmentIssueDescription}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '8px 16px',
                          fontSize: 14,
                          fontWeight: '500',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          cursor: aiImproving.equipmentIssueDescription ? 'not-allowed' : 'pointer',
                          opacity: aiImproving.equipmentIssueDescription ? 0.6 : 1
                        }}
                      >
                        <Sparkles size={16} />
                        {aiImproving.equipmentIssueDescription ? 'Improving...' : 'Suggest'}
                      </button>
                    )}
                  </div>
                  <textarea
                    value={currentIssue.description}
                    onChange={(e) => setCurrentIssue(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={t.describeIssue}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: 16,
                      fontSize: 20,
                      border: '2px solid #d1d5db',
                      borderRadius: 8,
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                  {aiIndicators.equipmentIssueDescription && (
                    <div style={{
                      marginTop: 8,
                      padding: '8px 12px',
                      background: aiIndicators.equipmentIssueDescription === 'translated' ? '#fef3c7' : '#dbeafe',
                      color: aiIndicators.equipmentIssueDescription === 'translated' ? '#92400e' : '#1e40af',
                      borderRadius: 6,
                      fontSize: 14,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      <Sparkles size={14} />
                      {aiIndicators.equipmentIssueDescription === 'translated' ? 'Translated from Spanish' : 'Text improved'}
                    </div>
                  )}
                  {aiSuggestions.equipmentIssueDescription && (
                    <div style={{
                      marginTop: 12,
                      padding: 16,
                      background: '#f0fdf4',
                      border: '2px solid #86efac',
                      borderRadius: 8
                    }}>
                      <div style={{ fontSize: 14, fontWeight: '600', marginBottom: 8, color: '#166534' }}>
                        ✨ Suggested improvement:
                      </div>
                      <div style={{ fontSize: 16, marginBottom: 12, color: '#166534' }}>
                        {aiSuggestions.equipmentIssueDescription}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => acceptSuggestion('equipmentIssueDescription', (val) => setCurrentIssue(prev => ({ ...prev, description: val })))}
                          style={{
                            padding: '8px 16px',
                            fontSize: 14,
                            fontWeight: '500',
                            background: '#16a34a',
                            color: 'white',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer'
                          }}
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          onClick={() => rejectSuggestion('equipmentIssueDescription')}
                          style={{
                            padding: '8px 16px',
                            fontSize: 14,
                            fontWeight: '500',
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer'
                          }}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 18, fontWeight: '600', marginBottom: 10 }}>
                    {t.severity} *
                  </label>
                  <CustomSelect
                    value={currentIssue.severity}
                    onChange={(value) => setCurrentIssue(prev => ({ ...prev, severity: value as 'low' | 'medium' | 'high' }))}
                    placeholder={t.severity}
                    options={[
                      { value: 'low', label: t.severityLow },
                      { value: 'medium', label: t.severityMedium },
                      { value: 'high', label: t.severityHigh }
                    ]}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 18, fontWeight: '600', marginBottom: 10 }}>
                    {t.addIssuePhoto}
                  </label>

                  {/* Photo Previews Grid */}
                  {currentIssuePhotoPreviews.length > 0 && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                      gap: 10,
                      marginBottom: 12
                    }}>
                      {currentIssuePhotoPreviews.map((url, index) => (
                        <div key={index} style={{ position: 'relative', paddingTop: '100%', borderRadius: 8, overflow: 'hidden' }}>
                          <img
                            src={url}
                            alt={`Issue photo ${index + 1}`}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          <button
                            onClick={() => removeIssuePhoto(index)}
                            style={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: 18,
                              fontWeight: 'bold',
                              lineHeight: 1
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Camera Button */}
                  <button
                    type="button"
                    onClick={handleTakeIssuePhoto}
                    disabled={issueCameraCooldown}
                    style={{
                      width: '100%',
                      padding: 16,
                      fontSize: 18,
                      fontWeight: '600',
                      backgroundColor: issueCameraCooldown ? '#d1d5db' : '#22c55e',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: issueCameraCooldown ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8
                    }}
                  >
                    <Camera size={20} />
                    {issueCameraCooldown ? 'Please wait...' : `${t.addIssuePhoto} (${currentIssue.photos.length})`}
                  </button>
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => {
                      setShowIssueForm(false);
                      setCurrentIssue({ equipment_id: null, description: '', severity: 'medium', photos: [] });
                      setCurrentIssuePhotoPreviews([]);
                    }}
                    style={{
                      flex: 1,
                      padding: 18,
                      fontSize: 20,
                      fontWeight: 'bold',
                      backgroundColor: '#e5e7eb',
                      color: '#374151',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveEquipmentIssue}
                    style={{
                      flex: 1,
                      padding: 18,
                      fontSize: 20,
                      fontWeight: 'bold',
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer'
                    }}
                  >
                    {t.saveIssue}
                  </button>
                </div>
              </div>
            )}

            {/* Reported Issues List */}
            {equipmentIssues.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 12 }}>
                  {t.reportedIssues} ({equipmentIssues.length})
                </h3>
                {equipmentIssues.map((issue, index) => (
                  <div
                    key={index}
                    style={{
                      padding: 16,
                      marginBottom: 12,
                      backgroundColor: issue.severity === 'high' ? '#fee2e2' : issue.severity === 'medium' ? '#fed7aa' : '#fef3c7',
                      border: `2px solid ${issue.severity === 'high' ? '#dc2626' : issue.severity === 'medium' ? '#f59e0b' : '#eab308'}`,
                      borderRadius: 8
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 4 }}>{issue.equipment_name}</div>
                        <div style={{ fontSize: 16, color: '#374151' }}>{issue.description}</div>
                        <div style={{ fontSize: 15, color: '#6b7280', marginTop: 4 }}>
                          {issue.severity === 'high' && t.severityHigh}
                          {issue.severity === 'medium' && t.severityMedium}
                          {issue.severity === 'low' && t.severityLow}
                        </div>
                      </div>
                      <button
                        onClick={() => removeEquipmentIssue(index)}
                        style={{
                          padding: '8px 14px',
                          fontSize: 16,
                          fontWeight: 'bold',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer'
                        }}
                      >
                        {t.removeIssue}
                      </button>
                    </div>
                  </div>
                ))}

                {!showIssueForm && (
                  <button
                    onClick={() => {
                      setShowIssueForm(true);
                      // Pre-select first equipment from selected list
                      if (selectedEquipment.length > 0) {
                        setCurrentIssue(prev => ({ ...prev, equipment_id: selectedEquipment[0] }));
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: 16,
                      fontSize: 18,
                      fontWeight: 'bold',
                      backgroundColor: '#fef3c7',
                      color: '#92400e',
                      border: '2px dashed #f59e0b',
                      borderRadius: 8,
                      cursor: 'pointer'
                    }}
                  >
                    + Add Another Issue
                  </button>
                )}
              </div>
            )}

            {/* Navigation buttons */}
            {!showIssueForm && (
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => goToStep(5)}
                  style={{
                    flex: 1,
                    padding: 20,
                    fontSize: 20,
                    fontWeight: 'bold',
                    backgroundColor: '#e5e7eb',
                    color: '#374151',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer'
                  }}
                >
                  {t.back}
                </button>
                <button
                  onClick={() => goToStep(7)}
                  style={{
                    flex: 1,
                    padding: 20,
                    fontSize: 20,
                    fontWeight: 'bold',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer'
                  }}
                >
                  {t.nextSiltFence}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 7: Silt Fence Inspection (renumbered from old Step 5) */}
        {step === 7 && (
          <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: 22, fontWeight: 'bold', marginBottom: 20, display: 'flex', alignItems: 'center' }}>
              <Wrench style={{ width: 24, height: 24, marginRight: 10 }} />
              {t.siltFenceInspection}
            </h2>

            {/* Condition Selector */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 12, display: 'block' }}>
                {t.siltFenceCondition}
              </label>
              <CustomSelect
                value={siltFenceCondition}
                onChange={(value) => setSiltFenceCondition(value)}
                placeholder="-- Select Condition --"
                options={[
                  { value: 'good', label: t.conditionGood },
                  { value: 'minor_repair', label: t.conditionMinorRepair },
                  { value: 'major_repair', label: t.conditionMajorRepair },
                  { value: 'replacement', label: t.conditionReplacement },
                  { value: 'not_present', label: t.conditionNotPresent }
                ]}
              />
            </div>

            {/* Repair Status */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 12, display: 'block' }}>
                {t.repairStatus}
              </label>
              <CustomSelect
                value={siltFenceRepairStatus}
                onChange={(value) => setSiltFenceRepairStatus(value)}
                placeholder={t.repairStatus}
                options={[
                  { value: 'none', label: t.repairStatusNone },
                  { value: 'needs_repair', label: t.repairStatusNeeds },
                  { value: 'repaired', label: t.repairStatusRepaired }
                ]}
              />
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <label style={{ fontSize: 18, fontWeight: 'bold' }}>
                  {t.siltFenceNotes}
                </label>
                {siltFenceNotes.trim() && !aiSuggestions.siltFenceNotes && (
                  <button
                    type="button"
                    onClick={() => improveText(siltFenceNotes, 'siltFenceNotes', setSiltFenceNotes, user?.role !== 'admin')}
                    disabled={aiImproving.siltFenceNotes}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '8px 16px',
                      fontSize: 14,
                      fontWeight: '500',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      cursor: aiImproving.siltFenceNotes ? 'not-allowed' : 'pointer',
                      opacity: aiImproving.siltFenceNotes ? 0.6 : 1
                    }}
                  >
                    <Sparkles size={16} />
                    {aiImproving.siltFenceNotes ? 'Improving...' : 'Suggest'}
                  </button>
                )}
              </div>
              <textarea
                value={siltFenceNotes}
                onChange={(e) => setSiltFenceNotes(e.target.value)}
                placeholder={t.addNotesAboutCondition}
                rows={4}
                style={{
                  width: '100%',
                  padding: 16,
                  fontSize: 18,
                  border: '2px solid #e5e7eb',
                  borderRadius: 8,
                  resize: 'vertical'
                }}
              />
              {aiIndicators.siltFenceNotes && (
                <div style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  background: aiIndicators.siltFenceNotes === 'translated' ? '#fef3c7' : '#dbeafe',
                  color: aiIndicators.siltFenceNotes === 'translated' ? '#92400e' : '#1e40af',
                  borderRadius: 6,
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}>
                  <Sparkles size={14} />
                  {aiIndicators.siltFenceNotes === 'translated' ? 'Translated from Spanish' : 'Text improved'}
                </div>
              )}
              {aiSuggestions.siltFenceNotes && (
                <div style={{
                  marginTop: 12,
                  padding: 16,
                  background: '#f0fdf4',
                  border: '2px solid #86efac',
                  borderRadius: 8
                }}>
                  <div style={{ fontSize: 14, fontWeight: '600', marginBottom: 8, color: '#166534' }}>
                    ✨ Suggested improvement:
                  </div>
                  <div style={{ fontSize: 16, marginBottom: 12, color: '#166534' }}>
                    {aiSuggestions.siltFenceNotes}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => acceptSuggestion('siltFenceNotes', setSiltFenceNotes)}
                      style={{
                        padding: '8px 16px',
                        fontSize: 14,
                        fontWeight: '500',
                        background: '#16a34a',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer'
                      }}
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      onClick={() => rejectSuggestion('siltFenceNotes')}
                      style={{
                        padding: '8px 16px',
                        fontSize: 14,
                        fontWeight: '500',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer'
                      }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Photos Section */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 12, display: 'block' }}>
                {t.siltFencePhotos}
              </label>

              {/* Previously Uploaded Silt Fence Photos */}
              {existingSiltFencePhotos.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                    {language === 'es' ? 'Fotos Subidas Anteriormente' : 'Previously Uploaded Photos'} ({existingSiltFencePhotos.length})
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: 12,
                    padding: 12,
                    backgroundColor: '#f0f9ff',
                    borderRadius: 8,
                    border: '2px solid #bfdbfe'
                  }}>
                    {existingSiltFencePhotos.map((url, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <img
                          src={url}
                          alt={`Silt fence ${index + 1}`}
                          style={{
                            width: '100%',
                            height: 120,
                            objectFit: 'cover',
                            borderRadius: 8
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add New Silt Fence Photos */}
              {existingSiltFencePhotos.length > 0 && (
                <h3 style={{ fontSize: 16, fontWeight: '600', marginBottom: 8, color: '#374151' }}>
                  {language === 'es' ? 'Agregar Nuevas Fotos' : 'Add New Photos'}
                </h3>
              )}

              <button
                onClick={handleTakeSiltFencePhoto}
                disabled={siltFenceCameraCooldown || siltFencePhotos.length >= 10}
                style={{
                  width: '100%',
                  padding: 24,
                  border: '2px dashed #cbd5e1',
                  borderRadius: 8,
                  cursor: (siltFenceCameraCooldown || siltFencePhotos.length >= 10) ? 'not-allowed' : 'pointer',
                  backgroundColor: '#f9fafb',
                  opacity: (siltFenceCameraCooldown || siltFencePhotos.length >= 10) ? 0.6 : 1,
                  marginBottom: 16
                }}
              >
                <Camera style={{ width: 40, height: 40, margin: '0 auto 10px', color: '#9ca3af' }} />
                <p style={{ fontSize: 18, color: '#4b5563', fontWeight: 'bold' }}>
                  {siltFenceCameraCooldown ? 'Please wait...' : siltFencePhotos.length >= 10 ? 'Max 10 photos' : t.addSiltFencePhoto}
                </p>
                <p style={{ fontSize: 16, color: '#6b7280' }}>{siltFencePhotos.length}/10 photos</p>
              </button>

              {/* Photo Previews */}
              {siltFencePhotoPreviews.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12 }}>
                  {siltFencePhotoPreviews.map((preview, index) => {
                    const uploadInfo = photoUploads.find(p => p.file === siltFencePhotos[index]);
                    const uploadStatus = uploadInfo?.uploadStatus || 'pending';
                    return (
                      <div key={index} style={{ position: 'relative' }}>
                        <img
                          src={preview}
                          alt={`Silt fence ${index + 1}`}
                          style={{
                            width: '100%',
                            height: 120,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: uploadStatus === 'uploaded' ? '3px solid #22c55e' : uploadStatus === 'failed' ? '3px solid #ef4444' : '3px solid transparent'
                          }}
                        />
                        {/* Upload status indicator */}
                        <div style={{
                          position: 'absolute',
                          bottom: 6,
                          left: 6,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: uploadStatus === 'uploaded' ? '#22c55e' : uploadStatus === 'uploading' ? '#3b82f6' : uploadStatus === 'failed' ? '#ef4444' : '#9ca3af',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                        }}>
                          {uploadStatus === 'uploaded' && <CheckCircle size={14} color="white" />}
                          {uploadStatus === 'uploading' && <Loader2 size={14} color="white" style={{ animation: 'spin 1s linear infinite' }} />}
                          {uploadStatus === 'failed' && <AlertCircle size={14} color="white" />}
                          {uploadStatus === 'pending' && <Cloud size={14} color="white" />}
                        </div>
                        <button
                          onClick={() => removeSiltFencePhoto(index)}
                          style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: 28,
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: 16,
                            fontWeight: 'bold'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Navigation */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => goToStep(6)}
                style={{
                  flex: 1,
                  padding: 20,
                  fontSize: 20,
                  fontWeight: 'bold',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                {t.back}
              </button>
              <button
                onClick={() => goToStep(8)}
                style={{
                  flex: 1,
                  padding: 20,
                  fontSize: 20,
                  fontWeight: 'bold',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                {t.nextPhotos}
              </button>
            </div>
          </div>
        )}

        {/* Step 8: Photos (renumbered from old Step 6) */}
        {step === 8 && (
          <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: 22, fontWeight: 'bold', marginBottom: 20, display: 'flex', alignItems: 'center' }}>
              <Camera style={{ width: 24, height: 24, marginRight: 10 }} />
              {t.dailyPhotos}
            </h2>

            {/* Previously Uploaded Photos */}
            {existingDailyPhotos.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 18, fontWeight: '600', marginBottom: 12, color: '#374151' }}>
                  {language === 'es' ? 'Fotos Subidas Anteriormente' : 'Previously Uploaded Photos'} ({existingDailyPhotos.length})
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 10,
                  padding: 16,
                  backgroundColor: '#f0f9ff',
                  borderRadius: 8,
                  border: '2px solid #bfdbfe'
                }}>
                  {existingDailyPhotos.map((url, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={url}
                        alt={`${language === 'es' ? 'Foto' : 'Photo'} ${index + 1}`}
                        style={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover',
                          borderRadius: 8
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add New Photos Section */}
            <h3 style={{ fontSize: 18, fontWeight: '600', marginBottom: 12, color: '#374151' }}>
              {language === 'es' ? 'Agregar Nuevas Fotos' : 'Add New Photos'}
            </h3>

            <button
              onClick={handleTakePhoto}
              disabled={cameraCooldown || photos.length >= 20}
              style={{
                width: '100%',
                border: '3px dashed #d1d5db',
                borderRadius: 12,
                padding: 40,
                textAlign: 'center',
                marginBottom: 24,
                cursor: (cameraCooldown || photos.length >= 20) ? 'not-allowed' : 'pointer',
                backgroundColor: '#f9fafb',
                opacity: (cameraCooldown || photos.length >= 20) ? 0.6 : 1
              }}
            >
              <Camera style={{ width: 56, height: 56, color: '#9ca3af', margin: '0 auto 16px' }} />
              <p style={{ fontSize: 20, color: '#374151', marginBottom: 6, fontWeight: '600' }}>
                {cameraCooldown ? 'Please wait...' : photos.length >= 20 ? 'Max 20 photos' : t.tapToAddPhotos}
              </p>
              <p style={{ fontSize: 16, color: '#6b7280' }}>{photos.length}/20 photos</p>
            </button>

            {photoPreviews.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 10,
                marginBottom: 24
              }}>
                {photoPreviews.map((preview, index) => {
                  const uploadInfo = photoUploads.find(p => p.file === photos[index]);
                  const uploadStatus = uploadInfo?.uploadStatus || 'pending';
                  return (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={preview}
                        alt={`${t.photo} ${index + 1}`}
                        style={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover',
                          borderRadius: 8,
                          border: uploadStatus === 'uploaded' ? '3px solid #22c55e' : uploadStatus === 'failed' ? '3px solid #ef4444' : '3px solid transparent'
                        }}
                      />
                      {/* Upload status indicator */}
                      <div style={{
                        position: 'absolute',
                        bottom: 6,
                        left: 6,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: uploadStatus === 'uploaded' ? '#22c55e' : uploadStatus === 'uploading' ? '#3b82f6' : uploadStatus === 'failed' ? '#ef4444' : '#9ca3af',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                      }}>
                        {uploadStatus === 'uploaded' && <CheckCircle size={14} color="white" />}
                        {uploadStatus === 'uploading' && <Loader2 size={14} color="white" style={{ animation: 'spin 1s linear infinite' }} />}
                        {uploadStatus === 'failed' && <AlertCircle size={14} color="white" />}
                        {uploadStatus === 'pending' && <Cloud size={14} color="white" />}
                      </div>
                      <button
                        onClick={() => removePhoto(index)}
                        style={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 22,
                          lineHeight: 1,
                          fontWeight: 'bold'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => goToStep(7)}
                style={{
                  flex: 1,
                  padding: 20,
                  fontSize: 20,
                  fontWeight: 'bold',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                {t.back}
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: 20,
                  fontSize: 22,
                  fontWeight: 'bold',
                  backgroundColor: loading ? '#d1d5db' : '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? t.saving : t.submitReport}
              </button>
            </div>
          </div>
        )}


        {/* Job Needs Selection Modal */}
        {showJobNeedsModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              zIndex: 1000,
              padding: 20,
              paddingTop: '5vh',
              paddingBottom: '40vh',
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}
            onClick={() => setShowJobNeedsModal(false)}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: 12,
                maxWidth: 600,
                width: '100%',
                maxHeight: '75vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                flexShrink: 0
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div style={{
                padding: 20,
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ fontSize: 20, fontWeight: 'bold', margin: 0 }}>
                  {t.selectItemsNeeded}
                </h3>
                <button
                  onClick={() => setShowJobNeedsModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4
                  }}
                >
                  <X size={24} color="#6b7280" />
                </button>
              </div>

              {/* Search Box */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
                <input
                  type="text"
                  value={jobNeedsSearch}
                  onChange={(e) => setJobNeedsSearch(e.target.value)}
                  placeholder={language === 'es' ? 'Buscar artículos...' : 'Search items...'}
                  style={{
                    width: '100%',
                    padding: 12,
                    fontSize: 16,
                    border: '2px solid #d1d5db',
                    borderRadius: 8,
                    outline: 'none'
                  }}
                />
              </div>

              {/* Items List */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: 20
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: 12
                }}>
                  {jobNeedsItems
                    .filter(item => {
                      if (!jobNeedsSearch.trim()) return true;
                      const searchLower = jobNeedsSearch.toLowerCase();
                      const nameEn = (item.item_name_en || '').toLowerCase();
                      const nameEs = (item.item_name_es || '').toLowerCase();
                      return nameEn.includes(searchLower) || nameEs.includes(searchLower);
                    })
                    .map((item) => (
                      <label
                        key={item.item_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: 12,
                          backgroundColor: selectedJobNeeds.includes(item.item_id) ? '#dbeafe' : '#f9fafb',
                          border: selectedJobNeeds.includes(item.item_id) ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                          borderRadius: 8,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedJobNeeds.includes(item.item_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedJobNeeds([...selectedJobNeeds, item.item_id]);
                            } else {
                              setSelectedJobNeeds(selectedJobNeeds.filter(id => id !== item.item_id));
                            }
                          }}
                          style={{
                            width: 18,
                            height: 18,
                            marginRight: 8,
                            cursor: 'pointer'
                          }}
                        />
                        <span style={{ fontSize: 15, fontWeight: '500' }}>
                          {language === 'es' ? item.item_name_es : item.item_name_en}
                        </span>
                      </label>
                    ))}
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: 20,
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: 15, color: '#6b7280' }}>
                  {selectedJobNeeds.length} {selectedJobNeeds.length === 1 ? 'item' : 'items'} selected
                </span>
                <button
                  onClick={() => {
                    setShowJobNeedsModal(false);
                    setJobNeedsSearch('');
                  }}
                  style={{
                    padding: '12px 24px',
                    fontSize: 16,
                    fontWeight: '600',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer'
                  }}
                >
                  {language === 'es' ? 'Listo' : 'Done'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Camera Component (Android only) */}
        {showCamera && (
          <CameraComponent
            onCapture={handleCameraCapture}
            onClose={handleCameraClose}
          />
        )}
      </div>
    </div>
  );
};

export default DailyReports;
